import { useEffect, useRef, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  BarChart3, Bell, Boxes, BriefcaseBusiness, Building2, Camera, CheckCircle2, ChevronRight, CircleDollarSign, ClipboardList, Download,
  FilePlus2, FileSignature, FileSpreadsheet, FileText, HardHat, Image as ImageIcon, KeyRound, Languages, LayoutDashboard, LogOut, Menu, Package, PackageCheck, Plus, Upload,
  Receipt, Search, Settings, ShoppingCart, Sparkles, Target, Truck, Users, Wrench, X, XCircle
} from "lucide-react";
import "@/App.css";
import { call, compact, date, dateTime, money, slug, formatError } from "@/lib/api";
import { Modal, TableToolbar, PageHeader, AccessDenied, RowEditActions } from "@/components/common";
import { AIInsightPanel } from "@/components/AIInsight";
import { useGeo, PhotoPicker, TwoSlotPicker, PhotoGallery, uploadPhotos } from "@/components/PhotoUpload";
import { ChangePasswordModal } from "@/components/ChangePassword";
import { exportInvoicePDF, exportPOPDF, exportExpenseRecapPDF } from "@/lib/pdf";
import VariationOrdersPage from "@/pages/VariationOrders";
import RABPage, { RABDetail } from "@/pages/RAB";
import QuotationsPage from "@/pages/Quotations";
import ManpowerPage from "@/pages/manpower/Manpower";
import { ProjectManpowerPanel } from "@/pages/manpower/ManpowerProjects";
import { CostFeePanel, CapGauge } from "@/components/CostFee";
import ToolsPage from "@/pages/Tools";
import MasterUsers, { pic } from "@/pages/MasterUsers";
import MasterDivisions from "@/pages/MasterDivisions";
import MasterRoles from "@/pages/MasterRoles";
import DataManagement from "@/pages/DataManagement";
import SettingsPage from "@/pages/Settings";
import HREmployees from "@/pages/hr/HREmployees";
import HRAttendance from "@/pages/hr/HRAttendance";
import HRPayroll, { HRPayslips } from "@/pages/hr/HRPayroll";
import { HRDashboard, HRApprovals, HRReports, HRSettings } from "@/pages/hr/HRReports";
import { CalendarCheck, Clock, Wallet, UserCog, SlidersHorizontal, ClipboardCheck } from "lucide-react";
import CashFlowPage from "@/pages/CashFlow";
import ResetPassword from "@/pages/ResetPassword";
import { ProjectProfitReport, ProjectProfitPanel } from "@/pages/ProjectProfit";
import ProjectWizard from "@/pages/ProjectWizard";
import { ProjectTeam } from "@/components/ProjectTeam";
import { NotificationBell } from "@/components/Notifications";
import { TaskPanel, MyTasks } from "@/components/Tasks";
import { SchedulePanel } from "@/components/Schedule";
import { DocumentsPanel } from "@/components/Documents";
import { exportWeeklyReportPDF } from "@/lib/weeklyReport";
import { exportDailyRecapPDF } from "@/lib/dailyRecap";
import { RabCurvePanel } from "@/components/RabCurve";

const T = {
  ID: {
    workspace: "WORKSPACE LOGIN", welcome: "Selamat datang kembali.", welcomeDesc: "Masuk untuk melihat operasi bisnis Anda hari ini.",
    email: "Email kerja", password: "Password", signIn: "Masuk ke workspace", syncOk: "Data tersinkronisasi",
    quickAdd: "Quick add", search: "Cari apa saja…", logout: "Keluar",
    nav: { dashboard: "Dashboard", crm: "CRM & Leads", tenders: "Tender", rab: "RAB / BOQ", quotations: "Quotation", contracts: "Kontrak", vo: "Variation Order",
      users: "User / Karyawan", master_manpower: "Master Man Power / Tukang", divisions: "Divisi", roles: "Role & Permission", dm: "Data Management",
      projects: "Projects", procurement: "Procurement", inventory: "Inventory", suppliers: "Supplier", employees: "Man Power / Tukang", progress: "Progress Report", tools: "Alat Kerja",
      equipment: "Alat Berat", reports_dr: "Daily Report", invoices: "Invoice", expenses: "Expense", cashflow: "Cash Flow",
      approvals: "Approval", reports: "Reports", settings: "Settings", hr_dashboard: "Dashboard HR", hr_employees: "Data Karyawan", hr_attendance: "Absensi", hr_overtime: "Lembur & Izin", hr_payroll: "Penggajian / Payroll", hr_payslips: "Slip Gaji", hr_settings: "Pengaturan Payroll", hr_reports: "Laporan HR", hr_approvals: "Approval HR" }
  },
  EN: {
    workspace: "WORKSPACE LOGIN", welcome: "Welcome back.", welcomeDesc: "Sign in to view your operations today.",
    email: "Work email", password: "Password", signIn: "Sign in to workspace", syncOk: "Data synced",
    quickAdd: "Quick add", search: "Search anything…", logout: "Logout",
    nav: { dashboard: "Dashboard", crm: "CRM & Leads", tenders: "Tender", rab: "RAB / BOQ", quotations: "Quotation", contracts: "Contract", vo: "Variation Order",
      users: "Users", master_manpower: "Master Man Power / Workers", divisions: "Divisions", roles: "Roles & Permissions", dm: "Data Management",
      projects: "Projects", procurement: "Procurement", inventory: "Inventory", suppliers: "Supplier", employees: "Man Power / Workers", progress: "Progress Report", tools: "Work Tools",
      equipment: "Equipment", reports_dr: "Daily Report", invoices: "Invoice", expenses: "Expense", cashflow: "Cash Flow",
      approvals: "Approval", reports: "Reports", settings: "Settings", hr_dashboard: "HR Dashboard", hr_employees: "Employees", hr_attendance: "Attendance", hr_overtime: "Overtime & Leave", hr_payroll: "Payroll", hr_payslips: "Payslips", hr_settings: "Payroll Settings", hr_reports: "HR Reports", hr_approvals: "HR Approvals" }
  }
};

const NAV = [
  { group: "OVERVIEW", items: [
    { id: "dashboard", key: "dashboard", icon: LayoutDashboard, live: true, perm: "dashboard" },
  ]},
  { group: "PRE-SALES", items: [
    { id: "leads", key: "crm", icon: Target, perm: "leads" },
    { id: "tenders", key: "tenders", icon: ClipboardList, perm: "tenders" },
    { id: "rabs", key: "rab", icon: FileSpreadsheet, perm: "rabs" },
    { id: "quotations", key: "quotations", icon: FileText, perm: "quotations" },
    { id: "contracts", key: "contracts", icon: FileSignature, perm: "contracts" },
  ]},
  { group: "OPERATIONS", items: [
    { id: "projects", key: "projects", icon: BriefcaseBusiness, perm: "projects" },
    { id: "variation-orders", key: "vo", icon: FilePlus2, perm: "variation-orders" },
    { id: "daily-reports", key: "reports_dr", icon: HardHat, perm: "daily-reports" },
    { id: "approvals", key: "approvals", icon: CheckCircle2, perm: "approvals" },
  ]},
  { group: "SUPPLY CHAIN", items: [
    { id: "procurement", key: "procurement", icon: ShoppingCart, perm: "purchase-orders" },
    { id: "inventory", key: "inventory", icon: Boxes, perm: "inventory" },
    { id: "suppliers", key: "suppliers", icon: Truck, perm: "suppliers" },
    { id: "tools", key: "tools", icon: Wrench, perm: "equipment" },
  ]},
  { group: "RESOURCES", items: [
    { id: "manpower", key: "employees", icon: Users, perm: "manpower", alt: ["manpower-attendance", "manpower-payment"] },
    { id: "hr-attendance", key: "hr_attendance", icon: CalendarCheck, perm: "attendance" },
    { id: "hr-overtime", key: "hr_overtime", icon: Clock, perm: "attendance" },
  ]},
  { group: "FINANCE", items: [
    { id: "cash-flow", key: "cashflow", icon: Building2, perm: "cash-flow" },
    { id: "invoices", key: "invoices", icon: Receipt, perm: "invoices" },
    { id: "expenses", key: "expenses", icon: CircleDollarSign, perm: "expenses" },
    { id: "reports", key: "reports", icon: BarChart3, perm: "reports" },
  ]},
  { group: "HR / PAYROLL", items: [
    { id: "hr-dashboard", key: "hr_dashboard", icon: LayoutDashboard, perm: "hr", alt: ["hr-payroll", "hr-approvals"] },
    { id: "hr-employees", key: "hr_employees", icon: UserCog, perm: "hr", alt: ["hr-payroll", "hr-approvals"] },
    { id: "hr-payroll", key: "hr_payroll", icon: Wallet, perm: "hr", alt: ["hr-payroll"] },
    { id: "hr-payslips", key: "hr_payslips", icon: Receipt, perm: "attendance" },
    { id: "hr-settings", key: "hr_settings", icon: SlidersHorizontal, perm: "hr" },
    { id: "hr-reports", key: "hr_reports", icon: BarChart3, perm: "hr", alt: ["hr-payroll"] },
    { id: "hr-approvals", key: "hr_approvals", icon: ClipboardCheck, perm: "hr-approvals", alt: ["hr"] },
  ]},
  { group: "MASTER DATA", items: [
    { id: "users", key: "users", icon: Users, perm: "users" },
    { id: "master-manpower", key: "master_manpower", icon: HardHat, perm: "manpower" },
    { id: "divisions", key: "divisions", icon: Building2, perm: "divisions" },
    { id: "roles", key: "roles", icon: CheckCircle2, perm: "roles" },
    { id: "data-management", key: "dm", icon: Boxes, perm: "data-management" },
  ]},
];
const PAGE_PERM = Object.fromEntries(NAV.flatMap(g => g.items).map(n => [n.id, n.perm]));
PAGE_PERM.settings = "settings";

function useToast() {
  const [toast, setToast] = useState(null);
  return {
    toast,
    show: (msg, type = "info") => { setToast({ msg, type }); setTimeout(() => setToast(null), 2800); },
    render: toast ? <div className={`toast ${toast.type}`} data-testid="toast-message">{toast.msg}</div> : null,
  };
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");
  const [sent, setSent] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { const u = await call.post("/auth/login", { email, password }); onLogin(u); }
    catch (x) { setErr(formatError(x, "Login gagal")); }
    finally { setLoading(false); }
  };
  const forgot = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { const r = await call.post("/auth/forgot-password", { email }); setSent(r.message); }
    catch (x) { setErr(formatError(x, "Gagal mengirim tautan reset")); }
    finally { setLoading(false); }
  };
  return (
    <main className="login-shell">
      <div className="login-art">
        <div className="login-logo-plate" data-testid="login-logo">
          <img src="/brand/pkm-logo.webp" alt="PT Prime Karya Mandiri" />
        </div>
        <div className="login-art-copy">
          <span>CONTRACTOR ERP</span>
          <h1>Build with<br /><em>clarity.</em></h1>
          <p>Satu ruang kendali untuk menggerakkan proyek konstruksi lebih jauh — dari lead hingga serah terima.</p>
        </div>
        <div className="login-art-meta">PT PRIME KARYA MANDIRI <span>•</span> 2026</div>
      </div>
      <div className="login-form-wrap">
        <div className="mobile-brand"><img src="/brand/pkm-logo.webp" alt="PT Prime Karya Mandiri" data-testid="login-logo-mobile" /></div>
        <div className="login-form">
          {mode === "forgot" ? <>
            <span className="eyebrow">LUPA PASSWORD</span>
            <h2>Atur ulang password.</h2>
            <p className="muted">Masukkan email kerja Anda. Kami akan mengirim tautan untuk membuat password baru (berlaku 60 menit).</p>
            {sent ? <div className="login-sent" data-testid="forgot-sent">
              <CheckCircle2 size={18} /> <span>{sent}</span>
            </div> : <form onSubmit={forgot}>
              <label>Email kerja
                <input data-testid="forgot-email-input" type="email" placeholder="nama@primekarya.co.id" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required />
              </label>
              {err && <div className="error" data-testid="forgot-error">{err}</div>}
              <button data-testid="forgot-submit-button" className="primary-btn" type="submit" disabled={loading}>
                {loading ? "Mengirim…" : "Kirim tautan reset"} <ChevronRight size={16} />
              </button>
            </form>}
            <button type="button" className="login-link" data-testid="back-to-login-button" onClick={() => { setMode("login"); setErr(""); setSent(""); }}>← Kembali ke halaman masuk</button>
          </> : <>
          <span className="eyebrow">WORKSPACE LOGIN</span>
          <h2>Selamat datang kembali.</h2>
          <p className="muted">Masuk untuk melihat operasi bisnis Anda hari ini.</p>
          <form onSubmit={submit}>
            <label>Email kerja
              <input data-testid="login-email-input" type="email" placeholder="nama@primekarya.co.id" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required />
            </label>
            <label>Password
              <input data-testid="login-password-input" type="password" placeholder="••••••••" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required />
            </label>
            <div className="login-aux"><button type="button" className="login-link" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setErr(""); }}>Lupa password?</button></div>
            {err && <div className="error" data-testid="login-error">{err}</div>}
            <button data-testid="login-submit-button" className="primary-btn" type="submit" disabled={loading}>
              {loading ? "Memverifikasi…" : "Masuk ke workspace"} <ChevronRight size={16} />
            </button>
          </form>
          </>}
        </div>
        <div className="login-footer">
          © 2026 Prime Karya Mandiri <span>•</span>
          <button data-testid="login-language-button">Bahasa Indonesia</button>
        </div>
      </div>
    </main>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [, setResetKey] = useState(0);
  const processed = useRef(false);
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      if (processed.current) return; processed.current = true;
      const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
      window.history.replaceState(null, "", window.location.pathname);
      call.post("/auth/session", { session_id: sessionId }).then(setUser).catch(() => {}).finally(() => setChecking(false));
      return;
    }
    call.get("/auth/me").then(setUser).catch(() => {}).finally(() => setChecking(false));
  }, []);
  if (checking) return <div className="loading-screen">Memuat workspace…</div>;
  if (window.location.pathname === "/reset-password") {
    const token = new URLSearchParams(window.location.search).get("token") || "";
    return <ResetPassword token={token} onDone={() => { window.history.replaceState(null, "", "/"); setUser(null); setResetKey(k => k + 1); }} />;
  }
  if (!user) return <Login onLogin={setUser} />;
  return (
    <BrowserRouter>
      <ERP user={user} onLogout={async () => { try { await call.post("/auth/logout", {}); } catch(e){} setUser(null); }} />
    </BrowserRouter>
  );
}

function ERP({ user, onLogout }) {
  const perms = user.permissions || [];
  const can = (p) => perms.includes(p);
  const visibleNav = NAV.map(g => ({ ...g, items: g.items.filter(n => can(n.perm) || (n.alt || []).some(can)) })).filter(g => g.items.length);
  const [collapsed, setCollapsed] = useState(false);
  const [lang, setLang] = useState("ID");
  const [active, setActive] = useState(visibleNav[0]?.items[0]?.id || "dashboard");
  const [detail, setDetail] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const t = T[lang];
  const flatNav = NAV.flatMap(g => g.items);
  const current = flatNav.find(n => n.id === active) || { key: active };
  const title = t.nav[current.key] || current.key;
  const { show, render } = useToast();

  return (
    <div className={`erp-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <aside className="sidebar">
        <div className="sidebar-brand" data-testid="sidebar-brand">
          <img className="brand-logo" src="/brand/pkm-mark.png" alt="PKM" />
          {!collapsed && <div><b>PRIME KARYA MANDIRI</b><span>CONTRACTOR ERP</span></div>}
        </div>
        <div className="workspace-switch">
          <span className="workspace-avatar"><img src="/brand/pkm-mark.png" alt="" /></span>
          {!collapsed && <><div><b>Prime Karya</b><small>Head Office</small></div><ChevronRight size={14} /></>}
        </div>
        <nav>
          {visibleNav.map(group => (
            <div key={group.group}>
              {!collapsed && <small className="nav-label">{group.group}</small>}
              {group.items.map(n => (
                <button
                  data-testid={`nav-${n.id}-button`}
                  className={active === n.id ? "active" : ""}
                  key={n.id}
                  onClick={() => { setActive(n.id); setDetail(null); }}
                  title={t.nav[n.key]}
                >
                  <n.icon size={18} />
                  {!collapsed && <span className="label">{t.nav[n.key]}</span>}
                  {n.live && !collapsed && <span className="live-dot" />}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-bottom">
          {can("settings") && <button data-testid="nav-settings-button" onClick={() => setActive("settings")}>
            <Settings size={18} />{!collapsed && <span className="label">{t.nav.settings}</span>}
          </button>}
          <button data-testid="change-password-button" onClick={() => setPwdOpen(true)} title="Ganti password">
            <KeyRound size={18} />{!collapsed && <span className="label">Ganti Password</span>}
          </button>
          <button data-testid="logout-button" onClick={onLogout}>
            <LogOut size={18} />{!collapsed && <span className="label">{t.logout}</span>}
          </button>
          <div className="user-mini">
            {user.picture ? <img className="avatar" src={pic(user.picture)} alt="" referrerPolicy="no-referrer" /> : <span className="avatar">{user.name.split(" ").map(x => x[0]).join("").slice(0, 2)}</span>}
            {!collapsed && <div><b>{user.name}</b><small data-testid="user-role-label">{user.position ? `${user.position} · ` : ""}{user.division}</small></div>}
          </div>
        </div>
      </aside>
      <section className="main-area">
        <header className="topbar">
          <button data-testid="sidebar-toggle-button" className="icon-btn" onClick={() => setCollapsed(!collapsed)}>
            <Menu size={20} />
          </button>
          <div className="crumb"><img className="crumb-logo" src="/brand/pkm-mark.png" alt="" data-testid="topbar-logo" /><span>PRIME KARYA</span><ChevronRight size={14} /><b>{title}</b></div>
          <div className="top-actions">
            <label className="global-search">
              <Search size={16} />
              <input data-testid="global-search-input" placeholder={t.search} />
              <kbd>⌘ K</kbd>
            </label>
            <button data-testid="language-toggle-button" className="lang-btn" onClick={() => setLang(lang === "ID" ? "EN" : "ID")}>
              <Languages size={14} />{lang}
            </button>
            <NotificationBell onNavigate={(id) => { setActive(id); setDetail(null); }} />
            <span className="top-avatar" title={user.name}>{user.name[0]}</span>
          </div>
        </header>
        <main className="content">
          <div className="page-heading">
            <div>
              <span className="eyebrow">{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
              <h1>{title}</h1>
            </div>
            <div className="heading-actions">
              <span className="sync-status"><i /> {t.syncOk}</span>
            </div>
          </div>
          <PageRouter active={active} user={user} lang={lang} detail={detail} setDetail={setDetail} toast={show} prefill={prefill} setPrefill={setPrefill} onNavigate={(id, pre) => { setPrefill(pre || null); setActive(id); }} />
        </main>
      </section>
      {pwdOpen && <ChangePasswordModal onClose={() => setPwdOpen(false)} toast={show} />}
      {render}
    </div>
  );
}

function PageRouter({ active, user, lang, detail, setDetail, toast, onNavigate, prefill, setPrefill }) {
  const perms = user.permissions || [];
  const navItem = NAV.flatMap(g => g.items).find(n => n.id === active);
  if (PAGE_PERM[active] && !perms.includes(PAGE_PERM[active]) && !(navItem?.alt || []).some(p => perms.includes(p))) return <AccessDenied module={active} />;
  if (detail?.module === "rab") return <RABDetail rabId={detail.id} onBack={() => setDetail(null)} onQuote={(r) => { setDetail(null); onNavigate("quotations", { rabId: r.id }); }} />;
  if (detail?.module === "project") return <ProjectDetail projectId={detail.id} user={user} onBack={() => setDetail(null)} />;
  switch (active) {
    case "dashboard": return <Dashboard user={user} />;
    case "leads": return <LeadsPage toast={toast} />;
    case "tenders": return <TendersPage toast={toast} />;
    case "rabs": return <RABPage onOpen={(id) => setDetail({ module:"rab", id })} toast={toast} />;
    case "quotations": return <QuotationsPage toast={toast} prefillRabId={prefill?.rabId} onPrefillUsed={() => setPrefill(null)} />;
    case "contracts": return <ContractsPage toast={toast} />;
    case "variation-orders": return <VariationOrdersPage toast={toast} user={user} />;
    case "projects": return <ProjectsPage onOpen={(id) => setDetail({ module:"project", id })} toast={toast} user={user} />;
    case "users": return <MasterUsers toast={toast} me={user} />;
    case "master-manpower": return <ManpowerPage toast={toast} me={user} masterOnly />;
    case "divisions": return <MasterDivisions toast={toast} />;
    case "roles": return <MasterRoles toast={toast} me={user} />;
    case "data-management": return <DataManagement toast={toast} me={user} />;
    case "daily-reports": return <DailyReportsPage toast={toast} me={user} />;
    case "approvals": return <ApprovalsPage toast={toast} />;
    case "procurement": return <ProcurementPage toast={toast} user={user} />;
    case "inventory": return <InventoryPage />;
    case "suppliers": return <SuppliersPage toast={toast} />;
    case "manpower": return <ManpowerPage toast={toast} me={user} onGoMaster={() => onNavigate("master-manpower")} />;
    case "equipment": return <EquipmentPage />;
    case "tools": return <ToolsPage toast={toast} user={user} />;
    case "invoices": return <InvoicesPage toast={toast} />;
    case "expenses": return <ExpensesPage toast={toast} />;
    case "cash-flow": return <CashFlowPage toast={toast} />;
    case "reports": return <ReportsPage toast={toast} />;
    case "settings": return <SettingsPage toast={toast} user={user} />;
    case "hr-dashboard": return <HRDashboard toast={toast} />;
    case "hr-employees": return <HREmployees toast={toast} me={user} />;
    case "hr-attendance": return <HRAttendance toast={toast} me={user} />;
    case "hr-overtime": return <HRAttendance toast={toast} me={user} mode="overtime" />;
    case "hr-payroll": return <HRPayroll toast={toast} me={user} />;
    case "hr-payslips": return <HRPayslips toast={toast} me={user} />;
    case "hr-settings": return <HRSettings toast={toast} />;
    case "hr-reports": return <HRReports toast={toast} />;
    case "hr-approvals": return <HRApprovals toast={toast} />;
    default: return <AccessDenied module={active} />;
  }
}

// ============= Dashboard =============
const chartData = [
  { month: "Sep", revenue: 2.1, cost: 1.5 }, { month: "Okt", revenue: 2.8, cost: 1.8 },
  { month: "Nov", revenue: 3.4, cost: 2.3 }, { month: "Des", revenue: 4.1, cost: 2.7 },
  { month: "Jan", revenue: 4.8, cost: 3.1 }, { month: "Feb", revenue: 5.6, cost: 3.7 },
];

const KPI_SETS = {
  super_admin: (k) => [
    { id: "total-users", label: "Total Users", value: `${k.active_users} / ${k.total_users}`, meta: "aktif / total", color: "dark" },
    { id: "total-divisions", label: "Total Divisions", value: k.total_divisions, meta: "master divisi", color: "blue" },
    { id: "total-projects", label: "Total Projects", value: k.total_projects, meta: `${k.active_projects} active · ${k.completed_projects} completed`, color: "green" },
    { id: "contract-value", label: "Contract Value", value: compact(k.contract_value), meta: `Profit est. ${compact(k.estimated_profit)}`, color: "dark" },
    { id: "receivable", label: "Receivable", value: compact(k.receivable), meta: `Payable ${compact(k.payable)}`, color: "orange" },
    { id: "cash-balance", label: "Cash Balance", value: compact(k.cash_balance), meta: `Inflow ${compact(k.cash_inflow)}`, color: "blue" },
    { id: "pending-approvals", label: "Pending Approvals", value: k.pending_approvals, meta: "Butuh keputusan", color: "orange" },
    { id: "leads-pipeline", label: "Leads Pipeline", value: compact(k.leads_pipeline), meta: "Potensi aktif", color: "green" },
  ],
  pm: (k) => [
    { id: "my-projects", label: "My Projects", value: k.my_projects, meta: `${k.total_projects} dapat diakses`, color: "dark" },
    { id: "active-projects", label: "Active Projects", value: k.active_projects, meta: `${k.completed_projects} completed`, color: "blue" },
    { id: "avg-progress", label: "Project Progress", value: `${k.avg_progress}%`, meta: "rata-rata", color: "green" },
    { id: "team-members", label: "Team Members", value: k.team_members, meta: "anggota aktif", color: "orange" },
  ],
  finance: (k) => [
    { id: "contract-value", label: "Project Finance", value: compact(k.contract_value), meta: `${k.total_projects} project`, color: "dark" },
    { id: "budget", label: "Budget", value: compact(k.budget), meta: "total budget", color: "blue" },
    { id: "actual-cost", label: "Actual Cost", value: compact(k.actual_cost), meta: "realisasi", color: "orange" },
    { id: "outstanding", label: "Outstanding Payment", value: compact(k.outstanding), meta: `${k.overdue_invoices} overdue`, color: "green" },
  ],
  procurement: (k) => [
    { id: "purchase-requests", label: "Purchase Request", value: k.purchase_requests, meta: `${k.pending_pr} pending`, color: "dark" },
    { id: "purchase-orders", label: "Purchase Order", value: k.purchase_orders, meta: `${k.delivered_po} delivered`, color: "blue" },
    { id: "po-progress", label: "Procurement Progress", value: `${k.po_progress}%`, meta: "PO terkirim", color: "green" },
    { id: "total-projects", label: "Projects", value: k.total_projects, meta: "dapat diakses", color: "orange" },
  ],
  staff: (k) => [
    { id: "my-projects", label: "My Projects", value: k.my_projects, meta: "project ditugaskan", color: "dark" },
    { id: "my-assignments", label: "My Assignments", value: k.my_assignments, meta: "penugasan aktif", color: "blue" },
    { id: "my-reports", label: "My Tasks", value: k.my_reports, meta: "daily report dibuat", color: "green" },
    { id: "avg-progress", label: "Progress", value: `${k.avg_progress}%`, meta: "rata-rata project", color: "orange" },
  ],
};

function Dashboard({ user }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { call.get("/dashboard").then(setData).catch(e => setErr(formatError(e, "Gagal memuat"))); }, []);
  if (err) return <div className="empty-module"><p>{err}</p></div>;
  if (!data) return <div className="empty-module"><p>Memuat data dashboard…</p></div>;
  const k = data.kpis; const variant = data.variant;
  const canAI = (user?.permissions || []).includes("ai");
  return (
    <div data-testid="dashboard-content" data-variant={variant}>
      <section className="kpi-grid">
        {(KPI_SETS[variant] || KPI_SETS.staff)(k).map(c => (
          <div className={`kpi-card ${c.color}`} data-testid={`kpi-${c.id}`} key={c.id}>
            <div className="kpi-top"><span>{c.label}</span><CircleDollarSign size={17} /></div>
            <strong>{c.value}</strong>
            <small><i /> {c.meta}</small>
          </div>
        ))}
      </section>
      <section className="dashboard-grid">
        {variant === "super_admin" ? (
          <div className="panel chart-panel">
            <div className="panel-head"><div><span className="eyebrow">ORGANISASI</span><h3>Users & Projects by Division</h3></div></div>
            <div className="dash-list" data-testid="users-by-division">
              {k.projects_by_division.map(d => { const u = k.users_by_division.find(x => x.division === d.division); return <div key={d.division}><span>{d.division}</span><b>{u?.count || 0} user · {d.count} project</b></div>; })}
            </div>
          </div>
        ) : (
          <div className="panel chart-panel">
            <div className="panel-head"><div><span className="eyebrow">PERFORMANCE</span><h3>Revenue vs Cost — 6 bulan</h3></div></div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2563eb" stopOpacity=".22" /><stop offset="1" stopColor="#2563eb" stopOpacity="0" /></linearGradient></defs>
                <CartesianGrid vertical={false} stroke="#e6eaf0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#8993a4", fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: "#8993a4", fontSize: 11 }} tickFormatter={v => `${v}M`} />
                <Tooltip formatter={v => `Rp ${v} M`} contentStyle={{ fontSize: 12, border: "1px solid #e5e9ef" }} />
                <Area type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={3} fill="url(#rev)" />
                <Area type="monotone" dataKey="cost" stroke="#f59e0b" strokeWidth={2} fill="none" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="panel alerts-panel">
          <div className="panel-head">
            <div><span className="eyebrow">{variant === "staff" ? "MY ASSIGNMENTS" : "ATTENTION NEEDED"}</span><h3>{variant === "staff" ? "Penugasan saya" : "Alerts"} <b className="count-badge">{data.alerts.length}</b></h3></div>
          </div>
          {data.alerts.length === 0 && <small style={{color:"#8c99a7"}}>Tidak ada notifikasi.</small>}
          {data.alerts.map((a, i) => (
            <div className="alert-row" data-testid={`alert-row-${i}`} key={i}>
              <span className={`alert-icon alert-${i % 3}`}><Bell size={14} /></span>
              <div><b>{a}</b><small>{variant === "staff" ? "Penugasan aktif" : "Prioritas tinggi · tinjau sekarang"}</small></div>
              <ChevronRight size={15} />
            </div>
          ))}
        </div>
      </section>
      <section className="panel projects-panel">
        <div className="panel-head">
          <div><span className="eyebrow">PORTFOLIO</span><h3>{variant === "staff" ? "Project saya" : "Project progress"}</h3></div>
        </div>
        <div className="project-table">
          <div className="table-head"><span>PROJECT</span><span>MANAGER</span><span>PROGRESS</span><span>HEALTH</span><span>{k.contract_value != null ? "CONTRACT" : "STATUS"}</span></div>
          {data.projects.length === 0 && <div className="division-empty">Belum ada project yang ditugaskan kepada Anda.</div>}
          {data.projects.map(p => (
            <div className="project-row" data-testid={`dashboard-project-${p.id}`} key={p.id}>
              <div>
                <span className="project-avatar">{p.name.split(" ").map(x => x[0]).join("").slice(0, 2)}</span>
                <div><b>{p.name}</b><small>{p.code} · {p.location}</small></div>
              </div>
              <span>{p.manager || "—"}</span>
              <div className="progress-wrap"><div className="progress"><i style={{ width: `${p.progress}%` }} /></div><b>{p.progress}%</b></div>
              <span className={`health ${p.health === "At risk" ? "risk" : ""}`}>{p.health}</span>
              <b>{p.contract_value != null ? compact(p.contract_value) : p.status}</b>
            </div>
          ))}
        </div>
      </section>
      {(variant === "staff" || variant === "pm") && <section style={{marginTop:16}}><MyTasks /></section>}
      {canAI && <section style={{marginTop:16}}><AIInsightPanel /></section>}
    </div>
  );
}

// ============= Leads =============
function LeadsPage({ toast }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY_LEAD = { company:"", contact:"", project:"", location:"", value:0, probability:50, status:"NEW LEAD", source:"Direct", pic:"", expected_start:"", notes:"" };
  const [form, setForm] = useState(EMPTY_LEAD);
  const load = () => call.get("/leads").then(setItems);
  useEffect(() => { load(); }, []);
  const submit = async () => {
    const body = { ...form, value: Number(form.value), probability: Number(form.probability) };
    try { if (editing) await call.put(`/leads/${editing}`, body); else await call.post("/leads", body); setOpen(false); setEditing(null); load(); toast(editing ? "Lead diperbarui" : "Lead berhasil ditambahkan"); }
    catch (e) { toast(formatError(e), "error"); }
  };
  const startEdit = (l) => { setEditing(l.id); setForm({ ...EMPTY_LEAD, ...Object.fromEntries(Object.keys(EMPTY_LEAD).map(k => [k, l[k] ?? EMPTY_LEAD[k]])) }); setOpen(true); };
  const remove = async (l) => { try { await call.delete(`/leads/${l.id}`); load(); toast("Lead dihapus (diarsipkan)"); } catch (e) { toast(formatError(e), "error"); } };
  return (
    <div>
      <PageHeader subtitle="Pantau peluang masuk — dari prospek awal hingga menjadi kontrak." />
      <TableToolbar count={items.length} filters={["Semua","Qualified","Negotiation","Won"]} onAdd={() => { setEditing(null); setForm(EMPTY_LEAD); setOpen(true); }} addLabel="Tambah Lead" />
      <div className="panel data-panel">
        <div className="data-list">
          {items.length === 0 && <div className="empty-module" style={{border:0,padding:60}}><p>Belum ada lead. Klik "Tambah Lead" untuk mulai.</p></div>}
          {items.map((l, i) => (
            <div className="data-item" data-testid={`leads-item-${i}`} key={l.id}>
              <div className="data-main">
                <span className="row-icon"><Target size={16} /></span>
                <div><b>{l.company}</b><small>{l.project} · {l.contact} · {l.location || "—"}</small></div>
              </div>
              <div className="data-value">{compact(l.value)}</div>
              <span className={`status status-${slug(l.status)}`}>{l.status}</span>
              <RowEditActions testid={`lead-${i}`} onEdit={() => startEdit(l)} onDelete={() => remove(l)} />
            </div>
          ))}
        </div>
      </div>
      {open && <Modal title={editing ? "Edit Lead" : "Tambah Lead Baru"} subtitle={editing ? `Perubahan dicatat di jejak audit · ${editing}` : "Masukkan informasi calon proyek"} onClose={() => { setOpen(false); setEditing(null); }} onSubmit={submit} testid="lead-modal">
        <div className="form-grid">
          <label>Perusahaan<input data-testid="lead-company-input" required value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></label>
          <label>Nama Kontak<input data-testid="lead-contact-input" required value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} /></label>
          <label>Nama Proyek<input data-testid="lead-project-input" required value={form.project} onChange={e => setForm({ ...form, project: e.target.value })} /></label>
          <label>Lokasi<input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></label>
          <label>Estimasi Nilai (Rp)<input type="number" required value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} /></label>
          <label>Probabilitas (%)<input type="number" min="0" max="100" value={form.probability} onChange={e => setForm({ ...form, probability: e.target.value })} /></label>
          <label>Status<select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            {["NEW LEAD","QUALIFIED","SITE VISIT","ESTIMATION","QUOTATION","NEGOTIATION","WON","LOST"].map(s => <option key={s}>{s}</option>)}
          </select></label>
          <label>Sumber<select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
            {["Direct","Referral","Website","Tender","Marketing"].map(s => <option key={s}>{s}</option>)}
          </select></label>
          <label className="form-full">Catatan<textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></label>
        </div>
      </Modal>}
    </div>
  );
}

// ============= Tenders =============
function TendersPage({ toast }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name:"", client:"", location:"", estimated_value:0, submission_deadline:"", duration_days:180, pic:"", notes:"", status:"Draft" });
  const load = () => call.get("/tenders").then(setItems);
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await call.post("/tenders", { ...form, estimated_value: Number(form.estimated_value), duration_days: Number(form.duration_days) }); setOpen(false); load(); toast("Tender dibuat"); }
    catch (e) { toast(e.response?.data?.detail || "Gagal", "error"); }
  };
  const daysLeft = (d) => d ? Math.max(0, Math.ceil((new Date(d) - new Date()) / 86400000)) : "-";
  return (
    <div>
      <PageHeader subtitle="Kelola tender aktif, dokumen, dan deadline pengajuan." />
      <TableToolbar count={items.length} filters={["Semua","Draft","Submitted","Won"]} onAdd={() => setOpen(true)} addLabel="Tambah Tender" />
      <div className="panel data-panel">
        <div className="data-list">
          {items.map((t, i) => (
            <div className="data-item" data-testid={`tenders-item-${i}`} key={t.id}>
              <div className="data-main">
                <span className="row-icon"><ClipboardList size={16} /></span>
                <div><b>{t.name}</b><small>{t.code} · {t.client} · {t.location} · Deadline {date(t.submission_deadline)} ({daysLeft(t.submission_deadline)} hari)</small></div>
              </div>
              <div className="data-value">{compact(t.estimated_value)}</div>
              <span className={`status status-${slug(t.status)}`}>{t.status}</span>
              <ChevronRight size={16} />
            </div>
          ))}
        </div>
      </div>
      {open && <Modal title="Tambah Tender" onClose={() => setOpen(false)} onSubmit={submit} testid="tender-modal">
        <div className="form-grid">
          <label>Nama Proyek<input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
          <label>Client<input required value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} /></label>
          <label>Lokasi<input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></label>
          <label>Nilai Estimasi (Rp)<input type="number" required value={form.estimated_value} onChange={e => setForm({ ...form, estimated_value: e.target.value })} /></label>
          <label>Deadline Submission<input type="date" required value={form.submission_deadline} onChange={e => setForm({ ...form, submission_deadline: e.target.value })} /></label>
          <label>Durasi Proyek (hari)<input type="number" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: e.target.value })} /></label>
          <label>PIC<input value={form.pic} onChange={e => setForm({ ...form, pic: e.target.value })} /></label>
          <label>Status<select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            {["Draft","Estimation","Internal Review","Submitted","Negotiation","Won","Lost"].map(s => <option key={s}>{s}</option>)}
          </select></label>
          <label className="form-full">Notes<textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></label>
        </div>
      </Modal>}
    </div>
  );
}

// ============= Contracts =============
function ContractsPage({ toast }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ customer:"", project_id:"", project_name:"", contract_value:0, start_date:"", end_date:"", retention_pct:5, advance_pct:20, warranty:"12 bulan" });
  const [docsFor, setDocsFor] = useState(null);
  const [docCounts, setDocCounts] = useState({});
  const loadCounts = (list) => Promise.all([...new Set(list.map(c => c.project_id).filter(Boolean))].map(pid => call.get(`/projects/${pid}/documents`).then(d => [pid, d.length]).catch(() => [pid, 0]))).then(r => setDocCounts(Object.fromEntries(r)));
  useEffect(() => { call.get("/contracts").then(d => { setItems(d); loadCounts(d); }); call.get("/projects").then(setProjects); }, []);
  const submit = async () => {
    try { await call.post("/contracts", { ...form, contract_value: Number(form.contract_value), retention_pct: Number(form.retention_pct), advance_pct: Number(form.advance_pct) }); setOpen(false); call.get("/contracts").then(setItems); toast("Kontrak dibuat"); }
    catch (e) { toast(e.response?.data?.detail || "Gagal", "error"); }
  };
  return (
    <div>
      <PageHeader subtitle="Dokumentasi kontrak, addendum, dan variation order — unggah dokumen kontrak (PDF/scan) per proyek yang telah didapatkan." />
      <TableToolbar count={items.length} filters={["Semua","Active","Completed"]} onAdd={() => setOpen(true)} addLabel="Tambah Kontrak" />
      <div className="panel data-panel">
        <div className="data-list">
          {items.map((c, i) => (
            <div className="data-item" data-testid={`contracts-item-${i}`} key={c.id}>
              <div className="data-main">
                <span className="row-icon"><FileSignature size={16} /></span>
                <div><b>{c.project_name}</b><small>{c.code} · {c.customer} · {date(c.start_date)} — {date(c.end_date)} · VO: {c.variation_orders}</small></div>
              </div>
              <div className="data-value">{compact(c.contract_value)}</div>
              <span className={`status status-${slug(c.status)}`}>{c.status}</span>
              <div className="row-actions">
                {c.project_id ? <button className="ghost-btn pdf-btn" data-testid={`contract-docs-${i}`} onClick={() => setDocsFor(c)}><Upload size={14} /> Dokumen{docCounts[c.project_id] ? <b className="count-badge">{docCounts[c.project_id]}</b> : null}</button> : <small className="muted">tanpa project</small>}
              </div>
            </div>
          ))}
        </div>
      </div>
      {docsFor && <div className="modal-backdrop" onClick={() => { setDocsFor(null); loadCounts(items); }}>
        <div className="modal modal-wide" data-testid="contract-docs-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-head"><div><h3>Dokumen Kontrak · {docsFor.code}</h3><small>{docsFor.project_name} · {docsFor.customer} — kontrak, addendum, SPK, BAST, dan lampiran lainnya</small></div>
            <button className="icon-btn" data-testid="contract-docs-modal-close" onClick={() => { setDocsFor(null); loadCounts(items); }}><X size={18} /></button></div>
          <div className="modal-body"><DocumentsPanel project={{ id: docsFor.project_id, name: docsFor.project_name }} canManage={true} isMember={true} toast={toast} /></div>
        </div>
      </div>}
      {open && <Modal title="Tambah Kontrak" onClose={() => setOpen(false)} onSubmit={submit} testid="contract-modal">
        <div className="form-grid">
          <label>Customer<input required value={form.customer} onChange={e => setForm({ ...form, customer: e.target.value })} /></label>
          <label>Project<select value={form.project_id} onChange={e => { const p = projects.find(x => x.id === e.target.value); setForm({ ...form, project_id: e.target.value, project_name: p?.name || "", contract_value: p?.contract_value || 0 }); }}>
            <option value="">Pilih project</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></label>
          <label>Contract Value (Rp)<input type="number" required value={form.contract_value} onChange={e => setForm({ ...form, contract_value: e.target.value })} /></label>
          <label>Warranty<input value={form.warranty} onChange={e => setForm({ ...form, warranty: e.target.value })} /></label>
          <label>Start Date<input type="date" required value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></label>
          <label>End Date<input type="date" required value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></label>
          <label>Retention (%)<input type="number" value={form.retention_pct} onChange={e => setForm({ ...form, retention_pct: e.target.value })} /></label>
          <label>Advance Payment (%)<input type="number" value={form.advance_pct} onChange={e => setForm({ ...form, advance_pct: e.target.value })} /></label>
        </div>
      </Modal>}
    </div>
  );
}

// ============= Projects =============
const PROJECT_STATUSES = ["Planning", "Active", "On Hold", "Completed", "Cancelled"];
const DR_WORK_TYPES = ["Pekerjaan Persiapan", "Pekerjaan Tanah & Pondasi", "Pekerjaan Struktur", "Pekerjaan Arsitektur", "Pekerjaan Finishing", "Pekerjaan MEP", "Pekerjaan Lansekap", "Pekerjaan Pembersihan"];
function ProjectStatusSelect({ project, canManage, onChanged, toast, testid }) {
  const [busy, setBusy] = useState(false);
  if (!canManage) return <span className={`status status-${slug(project.status)}`} data-testid={testid}>{project.status}</span>;
  const change = async (e) => {
    const status = e.target.value; if (status === project.status) return;
    if (status === "Completed" && !window.confirm(`Tandai project "${project.name}" sebagai Completed? Progress akan diset 100%.`)) return;
    setBusy(true);
    try { await call.put(`/projects/${project.id}`, { status }); toast(`Status ${project.code} → ${status}`); onChanged?.(); } catch (err) { toast(formatError(err), "error"); } finally { setBusy(false); }
  };
  return <select className={`status-select status-${slug(project.status)}`} data-testid={testid} value={project.status} disabled={busy} onClick={e => e.stopPropagation()} onChange={change} title="Ubah status project">{PROJECT_STATUSES.map(s => <option key={s}>{s}</option>)}</select>;
}
function ProjectsPage({ onOpen, toast, user }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pms, setPms] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [f, setF] = useState({ mine: 0, manager_id: "", division_id: "", status: "", date_from: "", date_to: "" });
  const load = (ff = f) => { const qs = Object.entries(ff).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&"); return call.get(`/projects${qs ? `?${qs}` : ""}`).then(setItems); };
  useEffect(() => { load(); call.get("/users?eligible=pm").then(setPms).catch(() => {}); call.get("/divisions").then(setDivisions).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setFilter = (k, v) => { const nf = { ...f, [k]: v }; setF(nf); load(nf); };
  return (
    <div>
      <PageHeader subtitle="Portofolio proyek sesuai hak akses Anda — pilih Project Manager, divisi, dan anggota tim saat membuat project." />
      <div className="module-toolbar">
        <div className="filter-tabs">
          <button data-testid="filter-all-projects" className={!f.mine ? "selected" : ""} onClick={() => setFilter("mine", 0)}>Semua Project<b>{items.length}</b></button>
          <button data-testid="filter-my-projects" className={f.mine ? "selected" : ""} onClick={() => setFilter("mine", 1)}>Project Saya</button>
        </div>
        {user.can_create_project && <button className="primary-btn small" data-testid="module-add-button" onClick={() => setOpen(true)}><Plus size={15} /> Buat Project</button>}
      </div>
      <div className="proj-filters" data-testid="project-filters">
        <select data-testid="filter-pm-select" value={f.manager_id} onChange={e => setFilter("manager_id", e.target.value)}><option value="">Semua PM</option>{pms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
        <select data-testid="filter-division-select" value={f.division_id} onChange={e => setFilter("division_id", e.target.value)}><option value="">Semua Divisi</option>{divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
        <select data-testid="filter-status-select" value={f.status} onChange={e => setFilter("status", e.target.value)}><option value="">Semua Status</option>{PROJECT_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
        <input type="date" data-testid="filter-from-input" value={f.date_from} onChange={e => setFilter("date_from", e.target.value)} title="Mulai dari" />
        <input type="date" data-testid="filter-to-input" value={f.date_to} onChange={e => setFilter("date_to", e.target.value)} title="Selesai sebelum" />
      </div>
      <div className="panel data-panel">
        <div className="data-list">
          {items.length === 0 && <div className="empty-module" style={{border:0,padding:60}}><p>Tidak ada project yang dapat Anda akses dengan filter ini.</p></div>}
          {items.map((p, i) => (
            <div className="data-item" data-testid={`projects-item-${i}`} key={p.id} onClick={() => onOpen(p.id)} style={{cursor:"pointer"}}>
              <div className="data-main">
                <span className="row-icon"><BriefcaseBusiness size={16} /></span>
                <div><b>{p.name}</b><small>{p.code} · {p.client} · {p.location} · PM {p.manager || "—"} · {(p.member_ids || []).length} anggota · Progress {p.progress}%</small></div>
              </div>
              <div className="data-value">{p.contract_value != null ? compact(p.contract_value) : `${p.progress}%`}</div>
              <ProjectStatusSelect project={p} canManage={["super_admin", "owner", "admin"].includes(user.role) || p.manager_id === user.id} onChanged={load} toast={toast} testid={`project-status-${i}`} />
              <ChevronRight size={16} />
            </div>
          ))}
        </div>
      </div>
      {open && <ProjectWizard toast={toast} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function ProjectDetail({ projectId, user, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [toastMsg, setToastMsg] = useState(null);
  const [weekEnd, setWeekEnd] = useState("");
  const load = () => call.get(`/projects/${projectId}`).then(setData).catch(e => setErr(formatError(e)));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const toast = (msg, type = "info") => { setToastMsg({ msg, type }); setTimeout(() => setToastMsg(null), 2800); };
  if (err) return <div className="empty-module" data-testid="project-denied"><p>{err}</p><button className="ghost-btn" onClick={onBack}>Kembali</button></div>;
  if (!data) return <div className="empty-module"><p>Memuat project…</p></div>;
  const { project: p, purchase_orders, invoices, daily_reports, expenses, photos, financial_visible, team, can_manage_team } = data;
  const isMember = (p.member_ids || []).includes(user.id) || p.manager_id === user.id;
  const weeklyPdf = async () => {
    toast("Menyusun laporan mingguan…");
    try { const [rep, company] = await Promise.all([call.get(`/projects/${projectId}/weekly-report${weekEnd ? `?week_end=${weekEnd}` : ""}`), call.get("/settings").catch(() => null)]); await exportWeeklyReportPDF(rep, company); toast("PDF laporan mingguan diunduh"); }
    catch (e) { toast(formatError(e, "Gagal membuat laporan"), "error"); }
  };
  const variance = (p.budget || 0) - (p.actual_cost || 0);
  const forecast_profit = (p.contract_value || 0) - (p.committed_cost || p.actual_cost || 0);
  const canAI = (user.permissions || []).includes("ai");
  return (
    <div data-testid="project-detail">
      <button className="ghost-btn" onClick={onBack} data-testid="project-detail-back"><ChevronRight size={14} style={{transform:"rotate(180deg)"}} /> Kembali ke Projects</button>
      <div className="weekly-bar" data-testid="weekly-report-bar">
        <span>Laporan mingguan klien · minggu berakhir</span>
        <input type="date" data-testid="weekly-week-end" value={weekEnd} onChange={e => setWeekEnd(e.target.value)} />
        <button className="primary-btn small" data-testid="weekly-report-button" onClick={weeklyPdf}><Download size={14} /> Laporan Mingguan (PDF)</button>
      </div>
      <div className="detail-strip" style={{marginTop:18}}>
        <div><span>CODE</span><b>{p.code}</b></div>
        <div><span>CLIENT</span><b>{p.client}</b></div>
        <div><span>MANAGER</span><b>{team?.manager?.name || p.manager || "—"}</b></div>
        <div><span>DURASI</span><b>{date(p.start_date)} — {date(p.end_date)}</b></div>
      </div>
      <div className="contract-type-strip" data-testid="project-contract-type">
        <span className={`status ${p.contract_type === "Cost and Fee" ? "status-quotation" : "status-active"}`}>{p.contract_type || "Lump Sum"}</span>
        <small>Jenis kontrak · {p.type}</small>
        <span className="contract-sep" />
        <small>Status project</small>
        <ProjectStatusSelect project={p} canManage={can_manage_team} onChanged={load} toast={toast} testid="project-detail-status" />
        {p.status_history?.length > 0 && <small title={p.status_history.map(h => `${h.from} → ${h.to} · ${h.by} · ${dateTime(h.at)}`).join("\n")}>terakhir diubah {p.status_history[p.status_history.length - 1].by}, {dateTime(p.status_history[p.status_history.length - 1].at)}</small>}
        {p.contract_type === "Cost and Fee" && p.cost_fee && <div className="cost-fee-detail" data-testid="project-cost-fee">
          <div><span>FEE</span><b>{p.cost_fee.fee_type === "Persentase" ? `${p.cost_fee.fee_value}% dari biaya` : compact(p.cost_fee.fee_value)}</b></div>
          <div><span>BATAS ANGGARAN</span><b>{p.cost_fee.budget_cap ? compact(p.cost_fee.budget_cap) : "—"}</b></div>
          <div><span>KOMPONEN BIAYA</span><b>{p.cost_fee.cost_components || "—"}</b></div>
          <div><span>PERSETUJUAN BIAYA TAMBAHAN</span><b>{p.cost_fee.approval_process || "—"}</b></div>
          {p.cost_fee.budget_cap > 0 && <div style={{ gridColumn: "1/-1" }}><CapGauge pct={Math.round((p.actual_cost || 0) / p.cost_fee.budget_cap * 1000) / 10} level={(p.actual_cost || 0) / p.cost_fee.budget_cap >= 1 ? "exceeded" : (p.actual_cost || 0) / p.cost_fee.budget_cap >= 0.8 ? "warning" : "ok"} cap={p.cost_fee.budget_cap} used={p.actual_cost || 0} remaining={p.cost_fee.budget_cap - (p.actual_cost || 0)} testid="project-cap-gauge" /></div>}
        </div>}
      </div>
      <div style={{marginTop:16}}><ProjectTeam project={p} team={team} canManage={can_manage_team} toast={toast} onChanged={load} /></div>
      <div style={{marginTop:16}}><RabCurvePanel projectId={projectId} /></div>
      <div className="dashboard-grid" style={{marginTop:16}}>
        <TaskPanel project={p} team={team} canManage={can_manage_team} toast={toast} />
        <SchedulePanel projectId={projectId} />
      </div>
      <div style={{marginTop:16}}><DocumentsPanel project={p} canManage={can_manage_team} isMember={isMember} toast={toast} /></div>
      {(user.permissions || []).some(x => ["manpower", "manpower-attendance"].includes(x)) && <div style={{marginTop:16}}><ProjectManpowerPanel projectId={projectId} toast={toast} canManage={can_manage_team || user.role === "supervisor"} /></div>}
      {toastMsg && <div className={`toast ${toastMsg.type}`} data-testid="toast-message">{toastMsg.msg}</div>}
      {financial_visible ? (
        <section className="kpi-grid" style={{marginTop:16}} data-testid="project-financial-kpis">
          <div className="kpi-card dark"><div className="kpi-top"><span>Contract Value</span></div><strong>{compact(p.contract_value)}</strong><small><i /> {p.status}</small></div>
          <div className="kpi-card blue"><div className="kpi-top"><span>Budget</span></div><strong>{compact(p.budget)}</strong><small><i /> Variance {compact(variance)}</small></div>
          <div className="kpi-card orange"><div className="kpi-top"><span>Committed Cost</span></div><strong>{compact(p.committed_cost || 0)}</strong><small><i /> Actual {compact(p.actual_cost)}</small></div>
          <div className="kpi-card green"><div className="kpi-top"><span>Forecast Profit</span></div><strong>{compact(forecast_profit)}</strong><small><i /> Margin {Math.round(forecast_profit/p.contract_value*100)}%</small></div>
        </section>
      ) : (
        <section className="kpi-grid" style={{marginTop:16}} data-testid="project-ops-kpis">
          <div className="kpi-card blue"><div className="kpi-top"><span>Progress</span></div><strong>{p.progress}%</strong><small><i /> {p.health}</small></div>
          <div className="kpi-card dark"><div className="kpi-top"><span>Daily Reports</span></div><strong>{daily_reports.length}</strong><small><i /> {photos.length} foto lapangan</small></div>
          <div className="kpi-card orange"><div className="kpi-top"><span>Purchase Orders</span></div><strong>{purchase_orders.length}</strong><small><i /> Data finansial dibatasi</small></div>
        </section>
      )}
      <div className="dashboard-grid" style={{marginTop:16}}>
        <div className="panel">
          <div className="panel-head"><div><span className="eyebrow">OPERATIONS</span><h3>Daily Reports terbaru</h3></div></div>
          <div style={{marginTop:14}}>
            {daily_reports.length === 0 && <small style={{color:"#8c99a7"}}>Belum ada daily report</small>}
            {daily_reports.map(d => (
              <div className="alert-row" key={d.id}>
                <span className="alert-icon alert-2"><HardHat size={14} /></span>
                <div><b>{d.date} · {d.manpower} orang · progress {d.progress}%{d.photo_count ? ` · ${d.photo_count} foto` : ""}</b><small>{d.work_type ? `${d.work_type} · ` : ""}{d.work_completed}</small></div>
              </div>
            ))}
          </div>
        </div>
        <div className="panel">
          <div className="panel-head"><div><span className="eyebrow">FIELD EVIDENCE</span><h3>Foto lapangan <b className="count-badge">{photos.length}</b></h3></div></div>
          <div style={{marginTop:14}}><PhotoGallery photos={photos} /></div>
        </div>
      </div>
      {financial_visible && <div className="panel" style={{marginTop:16}}>
        <div className="panel-head"><div><span className="eyebrow">FINANCIAL</span><h3>Invoices · Expenses</h3></div></div>
        <div style={{marginTop:14}}>
          {invoices.map(i => (
            <div className="alert-row" key={i.id}>
              <span className="alert-icon alert-0"><Receipt size={14} /></span>
              <div><b>{i.code} · {compact(i.total)}</b><small>{i.description} · due {date(i.due_date)} · {i.status}</small></div>
            </div>
          ))}
          {expenses.slice(0,3).map(e => (
            <div className="alert-row" key={e.id}>
              <span className="alert-icon alert-1"><CircleDollarSign size={14} /></span>
              <div><b>{e.category} · {compact(e.amount)}</b><small>{e.description} · {date(e.date)}</small></div>
            </div>
          ))}
        </div>
      </div>}
      {financial_visible && <div style={{marginTop:16}}><ProjectProfitPanel projectId={projectId} toast={toast} /></div>}
      <div className="panel" style={{marginTop:16}}>
        <div className="panel-head"><div><span className="eyebrow">PROCUREMENT</span><h3>Purchase Orders proyek</h3></div></div>
        <div style={{marginTop:14}}>
          {purchase_orders.map(po => (
            <div className="data-item" key={po.id}>
              <div className="data-main"><span className="row-icon"><ShoppingCart size={14} /></span><div><b>{po.code} · {po.material}</b><small>{po.supplier} · {po.quantity} {po.unit}</small></div></div>
              <div className="data-value">{po.total != null ? compact(po.total) : "—"}</div>
              <span className={`status status-${slug(po.status)}`}>{po.status}</span>
              <ChevronRight size={14} />
            </div>
          ))}
        </div>
      </div>
      {canAI && <div style={{marginTop:16}}><AIInsightPanel projectId={projectId} /></div>}
    </div>
  );
}

// ============= Daily Reports =============
function DailyReportsPage({ toast, me }) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewPhotos, setViewPhotos] = useState([]);
  const geo = useGeo();
  const [editing, setEditing] = useState(null);
  const EMPTY_DR = { project_id:"", date: new Date().toISOString().slice(0, 10), weather:"Cerah", work_type:"", work_completed:"", manpower:0, material_used:"", material_shortage:"", equipment_used:"", problems:"", solution:"", notes:"", progress:0, progress_type:"Persentase", progress_volume:"", work_status:"Berjalan", safety_incident:"Tidak ada" };
  const [form, setForm] = useState(EMPTY_DR);
  const [rabTypes, setRabTypes] = useState([]);
  const [exp, setExp] = useState({ open: false, start: new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10), project_id: "", photos: true });
  const [exporting, setExporting] = useState(false);
  const expRows = items.filter(r => r.date >= exp.start && r.date <= exp.end && (!exp.project_id || r.project_id === exp.project_id));
  const runExport = async () => {
    if (exp.start > exp.end) { toast("Tanggal awal tidak boleh melewati tanggal akhir", "error"); return; }
    if (!expRows.length) { toast("Tidak ada daily report pada periode ini", "error"); return; }
    setExporting(true);
    try {
      const company = await call.get("/settings").catch(() => null);
      let photosByReport = null;
      if (exp.photos) { photosByReport = {}; for (const r of expRows.filter(r => r.photo_count > 0)) { try { photosByReport[r.id] = await call.get(`/daily-reports/${r.id}/photos`); } catch {} } }
      await exportDailyRecapPDF(expRows, { start: exp.start, end: exp.end, company, projects, projectName: projects.find(p => p.id === exp.project_id)?.name || "", photosByReport });
      toast("PDF rekap daily report diunduh"); setExp({ ...exp, open: false });
    } catch (e) { console.error(e); toast("Gagal membuat PDF", "error"); }
    finally { setExporting(false); }
  };
  useEffect(() => {
    if (!form?.project_id) { setRabTypes([]); return; }
    const pname = projects.find(p => p.id === form.project_id)?.name;
    const linked = projects.find(p => p.id === form.project_id)?.rab_id;
    call.get("/rabs").then(rs => { const mine = rs.filter(r => (linked && r.id === linked) || r.project_id === form.project_id || (!linked && r.project_name === pname)); setRabTypes([...new Set(mine.flatMap(r => r.sections.map(sec => sec.name)))]); }).catch(() => setRabTypes([]));
  }, [form?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = () => call.get("/daily-reports").then(setItems);
  useEffect(() => { load(); call.get("/daily-reports/project-options").then(setProjects).catch(() => call.get("/projects").then(setProjects).catch(() => {})); }, []);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...form, manpower: Number(form.manpower), progress: Number(form.progress) };
      const report = editing ? await call.put(`/daily-reports/${editing}`, body) : await call.post("/daily-reports", body);
      if (files.length) { await uploadPhotos(report.id, files, geo, call.upload, (n, total) => toast(`Upload foto ${n}/${total}…`)); }
      setOpen(false); setEditing(null); setFiles([]); load(); toast(editing ? "Daily report diperbarui" : files.length ? `Daily report & ${files.length} foto tersimpan` : "Daily report tersimpan");
    } catch (e) { toast(formatError(e), "error"); }
    finally { setBusy(false); }
  };
  const startEdit = (r) => { setEditing(r.id); setForm(Object.fromEntries(Object.keys(EMPTY_DR).map(k => [k, r[k] ?? EMPTY_DR[k]]))); setOpen(true); };
  const remove = async (r) => { try { await call.delete(`/daily-reports/${r.id}`); load(); toast("Daily report dihapus (diarsipkan)"); } catch (e) { toast(formatError(e), "error"); } };
  const openReport = async (r) => { setViewing(r); setViewPhotos([]); try { setViewPhotos(await call.get(`/daily-reports/${r.id}/photos`)); } catch (e) {} };
  const canDeletePhoto = (p) => ["pm", "supervisor", "admin", "owner", "super_admin"].includes(me?.role) || p.uploaded_by === me?.name;
  const deletePhoto = async (p) => {
    try { await call.delete(`/daily-reports/${p.report_id}/photos/${p.id}`); setViewPhotos(list => list.filter(x => x.id !== p.id)); setItems(list => list.map(r => r.id === p.report_id ? { ...r, photo_count: Math.max((r.photo_count || 1) - 1, 0) } : r)); toast("Foto lapangan dihapus"); }
    catch (e) { toast(formatError(e, "Gagal menghapus foto"), "error"); }
  };
  return (
    <div>
      <PageHeader subtitle="Catatan pekerjaan harian dari lapangan — manpower, material, masalah, dan foto bukti dengan GPS." />
      <div className="module-toolbar" style={{ marginBottom: 0 }}>
        <div className="filter-tabs"><button className="selected" data-testid="filter-semua-button">Semua<b>{items.length}</b></button></div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="ghost-btn" data-testid="dr-export-button" onClick={() => setExp({ ...exp, open: true })}><Download size={15} /> Export PDF Rekap</button>
          <button className="primary-btn small" data-testid="module-add-button" onClick={() => { setEditing(null); setForm(EMPTY_DR); setOpen(true); }}><Plus size={15} /> Daily Report</button>
        </div>
      </div>
      {exp.open && <Modal title="Export PDF Rekap Daily Report" subtitle="Pilih periode tanggal — laporan harian dicetak detail per hari sesuai data input" onClose={() => setExp({ ...exp, open: false })} onSubmit={runExport} busy={exporting} submitLabel={exporting ? "Membuat PDF…" : "Download PDF"} testid="dr-export-modal">
        <div className="form-grid">
          <label>Dari Tanggal<input type="date" required data-testid="dr-export-start" value={exp.start} max={exp.end || undefined} onChange={e => setExp({ ...exp, start: e.target.value })} /></label>
          <label>Sampai Tanggal<input type="date" required data-testid="dr-export-end" value={exp.end} min={exp.start || undefined} onChange={e => setExp({ ...exp, end: e.target.value })} /></label>
          <label className="form-full">Project<select data-testid="dr-export-project" value={exp.project_id} onChange={e => setExp({ ...exp, project_id: e.target.value })}><option value="">Semua project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
          <label className="form-full recap-check"><input type="checkbox" data-testid="dr-export-photos" checked={exp.photos} onChange={e => setExp({ ...exp, photos: e.target.checked })} /> Sertakan foto lapangan</label>
          <div className="form-full period-banner" data-testid="dr-export-preview">{expRows.length} laporan · {new Set(expRows.map(r => r.date)).size} hari · {expRows.reduce((s, r) => s + (Number(r.manpower) || 0), 0)} orang-hari pada periode ini</div>
        </div>
      </Modal>}
      <div className="panel data-panel">
        <div className="data-list">
          {items.map((r, i) => {
            const project = projects.find(p => p.id === r.project_id);
            return (
              <div className="data-item" data-testid={`daily-reports-item-${i}`} key={r.id} onClick={() => openReport(r)} style={{cursor:"pointer"}}>
                <div className="data-main">
                  <span className="row-icon"><HardHat size={16} /></span>
                  <div><b>{project?.name || r.project_id} — {r.date}</b><small>{r.work_type ? `${r.work_type} · ` : ""}{r.work_completed} · {r.manpower} orang · cuaca {r.weather}</small></div>
                </div>
                <div className="data-value">{r.progress}% progress</div>
                <span className="chip photo-chip" data-testid={`dr-photo-count-${i}`}><Camera size={11} /> {r.photo_count || 0}</span>
                <span className="status status-active">{r.author}</span>
                <RowEditActions testid={`dr-${i}`} onEdit={() => startEdit(r)} onDelete={() => remove(r)} />
              </div>
            );
          })}
        </div>
      </div>
      {viewing && <Modal title={`Daily Report · ${viewing.date}`} subtitle={`${projects.find(p => p.id === viewing.project_id)?.name || viewing.project_id} · oleh ${viewing.author}`} onClose={() => setViewing(null)} wide testid="dr-view-modal">
        <div className="detail-strip">
          <div><span>CUACA</span><b>{viewing.weather}</b></div>
          <div><span>MANPOWER</span><b>{viewing.manpower} orang</b></div>
          <div><span>PROGRESS</span><b>{viewing.progress}%</b></div>
          <div><span>SAFETY</span><b>{viewing.safety_incident}</b></div>
        </div>
        {viewing.work_type && <p style={{fontSize:13,color:"#172236",margin:"0 0 6px"}}><b>Jenis Pekerjaan:</b> {viewing.work_type}</p>}
        <p style={{fontSize:13,color:"#172236",margin:"0 0 6px"}}><b>Pekerjaan:</b> {viewing.work_completed}</p>
        {viewing.material_used && <p style={{fontSize:12,color:"#4a5a73",margin:"0 0 4px"}}><b>Material:</b> {viewing.material_used}</p>}
        {viewing.problems && <p style={{fontSize:12,color:"#b87510",margin:"0 0 14px"}}><b>Masalah:</b> {viewing.problems}</p>}
        <small className="eyebrow">FOTO LAPANGAN ({viewPhotos.length})</small>
        <div style={{marginTop:10}}><PhotoGallery photos={viewPhotos} onDelete={deletePhoto} canDelete={canDeletePhoto} /></div>
      </Modal>}
      {open && <Modal title={editing ? "Edit Daily Report" : "Daily Report"} subtitle={editing ? "Perubahan dicatat di jejak audit" : "Isi laporan harian dari lapangan"} onClose={() => { setOpen(false); setEditing(null); }} onSubmit={submit} busy={busy} testid="dr-modal">
        <div className="form-grid">
          <label className="form-full">Project<select required data-testid="dr-project-select" value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}>
            <option value="">{projects.length ? "Pilih project" : "Memuat daftar project…"}</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.code ? ` · ${p.code}` : ""}{p.status && p.status !== "Active" ? ` (${p.status})` : ""}</option>)}
          </select></label>
          <label>Tanggal<input type="date" data-testid="dr-date-input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></label>
          <label>Kondisi Cuaca<select data-testid="dr-weather-select" value={form.weather} onChange={e => setForm({ ...form, weather: e.target.value })}>
            {["Cerah","Berawan","Hujan Ringan","Hujan Deras","Mendung"].map(s => <option key={s}>{s}</option>)}
          </select></label>
          <label>Progress (%)<input type="number" min="0" max="100" data-testid="dr-progress-input" value={form.progress} onChange={e => setForm({ ...form, progress: e.target.value })} /></label>
          <label>Jenis Progress<select value={form.progress_type} onChange={e => setForm({ ...form, progress_type: e.target.value })}>{["Persentase","Volume","Status"].map(s => <option key={s}>{s}</option>)}</select></label>
          <label>Volume Pekerjaan<input placeholder="mis. 120 m² lantai" value={form.progress_volume} onChange={e => setForm({ ...form, progress_volume: e.target.value })} /></label>
          <label>Status Pekerjaan<select value={form.work_status} onChange={e => setForm({ ...form, work_status: e.target.value })}>{["Berjalan","Selesai","Tertunda","Belum Mulai"].map(s => <option key={s}>{s}</option>)}</select></label>
          <label>Jumlah Man Power <small style={{textTransform:"none",fontWeight:500,color:"#9aa5b4"}}>(0 = otomatis dari absensi tukang)</small><input type="number" data-testid="dr-manpower-input" value={form.manpower} onChange={e => setForm({ ...form, manpower: e.target.value })} /></label>
          <label>Safety Incident<input value={form.safety_incident} onChange={e => setForm({ ...form, safety_incident: e.target.value })} /></label>
          <label className="form-full">Jenis Pekerjaan{rabTypes.length > 0 ? <>
            <select required data-testid="dr-work-type-select" value={form.work_type} onChange={e => setForm({ ...form, work_type: e.target.value })}>
              <option value="">— pilih jenis pekerjaan sesuai RAB —</option>
              {rabTypes.map(t => <option key={t} value={t}>{t}</option>)}
              {form.work_type && !rabTypes.includes(form.work_type) && <option value={form.work_type}>{form.work_type} (tidak ada di RAB)</option>}
            </select>
            <small style={{textTransform:"none",fontWeight:500,color:"#9aa5b4"}}>Pilihan diambil dari jenis pekerjaan RAB project ini agar bobot progress Kurva S selalu cocok</small></>
            : <>
            <input list="dr-work-types" required data-testid="dr-work-type-input" placeholder="mis. Pekerjaan Struktur, Pekerjaan Arsitektur, MEP" value={form.work_type} onChange={e => setForm({ ...form, work_type: e.target.value })} />
            <datalist id="dr-work-types">{DR_WORK_TYPES.map(t => <option key={t} value={t} />)}</datalist>
            <small style={{textTransform:"none",fontWeight:500,color:"#9aa5b4"}}>Project ini belum terhubung ke RAB — ketik jenis pekerjaan secara manual</small></>}
          </label>
          <label className="form-full">Pekerjaan yang Dilakukan<textarea required data-testid="dr-work-input" value={form.work_completed} onChange={e => setForm({ ...form, work_completed: e.target.value })} /></label>
          <label>Material yang Digunakan<input data-testid="dr-material-input" value={form.material_used} onChange={e => setForm({ ...form, material_used: e.target.value })} /></label>
          <label>Material yang Kurang<input data-testid="dr-shortage-input" value={form.material_shortage} onChange={e => setForm({ ...form, material_shortage: e.target.value })} /></label>
          <label className="form-full">Equipment<input value={form.equipment_used} onChange={e => setForm({ ...form, equipment_used: e.target.value })} /></label>
          <label>Kendala<textarea data-testid="dr-problems-input" value={form.problems} onChange={e => setForm({ ...form, problems: e.target.value })} /></label>
          <label>Solusi / Tindakan<textarea data-testid="dr-solution-input" value={form.solution} onChange={e => setForm({ ...form, solution: e.target.value })} /></label>
          <label className="form-full">Catatan<textarea data-testid="dr-notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></label>
          <PhotoPicker files={files} setFiles={setFiles} geo={geo} />
        </div>
      </Modal>}
    </div>
  );
}

// ============= Approvals =============
function ApprovalsPage({ toast }) {
  const [items, setItems] = useState([]);
  const load = () => call.get("/approvals").then(setItems);
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    try { await call.post(`/approvals/${id}/decide`, { decision, comment: decision === "approve" ? "Approved" : "Rejected" }); load(); toast(decision === "approve" ? "Approved" : "Rejected"); }
    catch (e) { toast(e.response?.data?.detail || "Gagal", "error"); }
  };
  const pending = items.filter(a => a.status === "Pending");
  const done = items.filter(a => a.status !== "Pending");
  return (
    <div>
      <PageHeader subtitle="Alur persetujuan untuk purchase order, purchase request, payment, dan quotation." />
      <div className="panel data-panel" data-testid="approvals-pending">
        <div className="panel-head" style={{padding:"18px 22px"}}>
          <div><span className="eyebrow">PENDING APPROVAL</span><h3>Butuh keputusan Anda <b className="count-badge">{pending.length}</b></h3></div>
        </div>
        {pending.length === 0 && <div style={{padding:"28px 22px",textAlign:"center",color:"#8c99a7",fontSize:12}}>Semua approval sudah diputuskan ✓</div>}
        {pending.map((a, i) => (
          <div className="approval-row" data-testid={`approval-${i}`} key={a.id}>
            <div><b>{a.title}</b><small>{a.reference_type.toUpperCase()} · {a.reference_id} · diminta oleh {a.requester}</small></div>
            <span className="chip">{compact(a.amount)}</span>
            <span className="status status-pending">{a.status}</span>
            <div className="approval-actions">
              <button className="approve-btn" data-testid={`approve-${i}`} onClick={() => decide(a.id, "approve")}><CheckCircle2 size={13} /> Approve</button>
              <button className="reject-btn" data-testid={`reject-${i}`} onClick={() => decide(a.id, "reject")}><XCircle size={13} /> Reject</button>
            </div>
          </div>
        ))}
      </div>
      {done.length > 0 && <div className="panel data-panel" style={{marginTop:16}}>
        <div className="panel-head" style={{padding:"18px 22px"}}>
          <div><span className="eyebrow">RIWAYAT</span><h3>Approval terakhir</h3></div>
        </div>
        {done.slice(0,8).map(a => (
          <div className="approval-row" key={a.id}>
            <div><b>{a.title}</b><small>{a.reference_id} · {a.requester} · {a.decided_by ? `oleh ${a.decided_by}` : ""}</small></div>
            <span className="chip">{compact(a.amount)}</span>
            <span className={`status status-${slug(a.status)}`}>{a.status}</span>
            <small style={{fontSize:11,color:"#8c99a7"}}>{date(a.decided_at)}</small>
          </div>
        ))}
      </div>}
    </div>
  );
}

// ============= Procurement =============
function ProcurementPage({ toast, user }) {
  const can = (p) => (user?.permissions || []).includes(p);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY_ITEM = { material:"", quantity:1, unit:"pcs", unit_price:0 };
  const EMPTY_PO = { project_id:"", supplier:"", delivery_date:"", notes:"", items:[{ ...EMPTY_ITEM }] };
  const [form, setForm] = useState(EMPTY_PO);
  const load = () => call.get("/purchase-orders").then(setItems);
  useEffect(() => { load(); call.get("/projects").then(setProjects); call.get("/suppliers").then(setSuppliers); }, []);
  const setItem = (i, patch) => setForm({ ...form, items: form.items.map((it, j) => j === i ? { ...it, ...patch } : it) });
  const poTotal = form.items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const submit = async () => {
    const body = { ...form, items: form.items.map(it => ({ ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price) })) };
    try { if (editing) await call.put(`/purchase-orders/${editing}`, body); else await call.post("/purchase-orders", body); setOpen(false); setEditing(null); load(); toast(editing ? "PO diperbarui (total & approval disesuaikan)" : "PO dibuat & dikirim untuk approval"); }
    catch (e) { toast(formatError(e), "error"); }
  };
  const startEdit = (p) => { setEditing(p.id); setForm({ project_id: p.project_id, supplier: p.supplier, delivery_date: p.delivery_date, notes: p.notes || "", items: (p.items?.length ? p.items : [{ material: p.material, quantity: p.quantity, unit: p.unit, unit_price: p.unit_price }]).map(it => ({ material: it.material, quantity: it.quantity, unit: it.unit || "pcs", unit_price: it.unit_price })) }); setOpen(true); };
  const remove = async (p) => { try { await call.delete(`/purchase-orders/${p.id}`); load(); toast("PO dihapus (diarsipkan)"); } catch (e) { toast(formatError(e), "error"); } };
  const [paying, setPaying] = useState(null);
  const [pay, setPay] = useState({ amount: 0, date: new Date().toISOString().slice(0, 10), method: "Transfer Bank", notes: "" });
  const [company, setCompany] = useState(null);
  useEffect(() => { call.get("/settings").then(setCompany).catch(() => {}); }, []);
  const exportPdf = async (p) => { try { await exportPOPDF(p, company, projects.find(x => x.id === p.project_id), suppliers.find(s => s.company === p.supplier)); toast(`PDF ${p.code} diunduh`); } catch (e) { toast("Gagal membuat PDF", "error"); } };
  const [receiving, setReceiving] = useState(null); const [rcv, setRcv] = useState(null);
  const startReceive = async (p) => { try { const d = await call.get(`/purchase-orders/${p.id}/receipts`); setReceiving({ po: p, ...d }); setRcv({ date: new Date().toISOString().slice(0, 10), warehouse: d.inventory[0]?.warehouse || "Gudang Utama", notes: "", lines: d.lines.map((l, i) => { const auto = d.inventory.find(v => v.name.toLowerCase() === (l.material || "").toLowerCase()); return { index: i, quantity: Math.max(l.quantity - (l.received_qty || 0), 0), inventory_id: auto?.id || "", warehouse: "" }; }) }); } catch (e) { toast(formatError(e), "error"); } };
  const submitReceive = async () => { try { const r = await call.post(`/purchase-orders/${receiving.po.id}/receive`, { ...rcv, lines: rcv.lines.map(l => ({ ...l, quantity: Number(l.quantity) })) }); setReceiving(null); load(); toast(`Barang diterima · ${r.receive_status} · stok inventory diperbarui`); } catch (e) { toast(formatError(e), "error"); } };
  const startPay = (p) => { setPaying(p); setPay({ amount: Math.round(p.total - (p.paid || 0)), date: new Date().toISOString().slice(0, 10), method: "Transfer Bank", notes: "" }); };
  const submitPay = async () => { try { const r = await call.post(`/purchase-orders/${paying.id}/pay`, { ...pay, amount: Number(pay.amount) }); setPaying(null); load(); toast(`Pembayaran ${paying.code} dicatat · ${r.payment_status} · outflow kas tercatat`); } catch (e) { toast(formatError(e), "error"); } };
  return (
    <div>
      <PageHeader subtitle="Purchase order, committed cost, dan pembayaran ke supplier (tercatat sebagai outflow kas)." />
      <TableToolbar count={items.length} filters={["Semua","Pending","Approved","Delivered"]} onAdd={() => { setEditing(null); setForm(EMPTY_PO); setOpen(true); }} addLabel="Buat PO" />
      <div className="panel data-panel">
        <div className="data-list">
          {items.map((p, i) => (
            <div className="data-item" data-testid={`procurement-item-${i}`} key={p.id}>
              <div className="data-main">
                <span className="row-icon"><ShoppingCart size={16} /></span>
                <div><b>{p.material}</b><small>{p.code} · {p.supplier} · {p.items?.length > 1 ? `${p.items.length} material` : `${p.quantity} ${p.unit}`} · delivery {date(p.delivery_date)}{p.paid ? ` · dibayar ${compact(p.paid)} (${p.payment_status})` : ""}</small>{p.items?.length > 1 && <small data-testid={`po-items-${i}`}>{p.items.map(it => `${it.material} ${it.quantity} ${it.unit}`).join(" · ")}</small>}</div>
              </div>
              <div className="data-value">{p.total != null ? compact(p.total) : "—"}</div>
              <span className={`status status-${slug(p.status)}`}>{p.status}{p.receive_status ? <small style={{display:"block",fontSize:9}}>{p.receive_status}</small> : null}</span>
              <div className="row-actions">
              <button className="ghost-btn pdf-btn" data-testid={`po-pdf-${i}`} onClick={() => exportPdf(p)}><Download size={14} /> PDF</button>
              {["Approved","Delivered"].includes(p.status) && p.receive_status !== "Fully Received" && can("inventory") && <button className="ghost-btn pdf-btn" data-testid={`po-receive-${i}`} onClick={() => startReceive(p)}><PackageCheck size={14} /> Terima</button>}
              {["Approved","Delivered"].includes(p.status) && (p.paid || 0) < p.total - 0.01 && <button className="ghost-btn pdf-btn" data-testid={`po-pay-${i}`} onClick={() => startPay(p)}><CircleDollarSign size={14} /> Bayar</button>}
              <RowEditActions testid={`po-${i}`} locked={!["Pending Approval","Rejected"].includes(p.status)} onEdit={() => startEdit(p)} onDelete={() => remove(p)} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {receiving && rcv && <Modal title={`Terima Barang · ${receiving.po.code}`} subtitle={`${receiving.po.supplier} · stok inventory akan bertambah otomatis per baris`} onClose={() => setReceiving(null)} onSubmit={submitReceive} testid="po-receive-modal" submitLabel="Konfirmasi Penerimaan" wide>
        <div className="form-grid"><label>Tanggal terima<input type="date" data-testid="rcv-date" value={rcv.date} onChange={e => setRcv({ ...rcv, date: e.target.value })} /></label><label>Gudang<input data-testid="rcv-warehouse" value={rcv.warehouse} onChange={e => setRcv({ ...rcv, warehouse: e.target.value })} /></label><label className="form-full">Catatan / No. surat jalan<input value={rcv.notes} onChange={e => setRcv({ ...rcv, notes: e.target.value })} /></label></div>
        <div className="po-items" style={{marginTop:14}} data-testid="rcv-lines">
          <div className="po-item head rcv"><span>Material</span><span>Dipesan</span><span>Sudah</span><span>Terima sekarang</span><span>Item inventory</span></div>
          {receiving.lines.map((l, i) => { const rem = l.quantity - (l.received_qty || 0); return <div className="po-item rcv" key={i} data-testid={`rcv-line-${i}`}>
            <span><b>{l.material}</b><br /><small className="muted">sisa {rem} {l.unit}</small></span><span>{l.quantity} {l.unit}</span><span>{l.received_qty || 0}</span>
            <input type="number" min="0" max={rem} step="any" data-testid={`rcv-qty-${i}`} disabled={rem <= 0} value={rcv.lines[i].quantity} onChange={e => setRcv({ ...rcv, lines: rcv.lines.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x) })} />
            <select data-testid={`rcv-inv-${i}`} value={rcv.lines[i].inventory_id} onChange={e => setRcv({ ...rcv, lines: rcv.lines.map((x, j) => j === i ? { ...x, inventory_id: e.target.value } : x) })}><option value="">+ Buat item inventory baru</option>{receiving.inventory.map(v => <option key={v.id} value={v.id}>{v.name} ({v.stock} {v.unit}) · {v.warehouse}</option>)}</select>
          </div>; })}
        </div>
        {receiving.receipts.length > 0 && <small className="muted" style={{display:"block",marginTop:12}}>Riwayat: {receiving.receipts.map(r => `${date(r.date)} oleh ${r.received_by} (${r.lines.map(l => `${l.material} ${l.quantity}`).join(", ")})`).join(" · ")}</small>}
      </Modal>}
      {paying && <Modal title={`Bayar PO ${paying.code}`} subtitle={`${paying.supplier} · ${paying.material} · total ${compact(paying.total)} · sisa ${compact(paying.total - (paying.paid || 0))}`} onClose={() => setPaying(null)} onSubmit={submitPay} testid="po-pay-modal" submitLabel="Catat Pembayaran">
        <div className="form-grid">
          <label>Nominal (Rp)<input type="number" min="1" max={Math.round(paying.total - (paying.paid || 0))} required data-testid="po-pay-amount" value={pay.amount} onChange={e => setPay({ ...pay, amount: e.target.value })} /></label>
          <label>Tanggal<input type="date" required data-testid="po-pay-date" value={pay.date} onChange={e => setPay({ ...pay, date: e.target.value })} /></label>
          <label>Metode<select value={pay.method} onChange={e => setPay({ ...pay, method: e.target.value })}>{["Transfer Bank","Giro","Tunai"].map(m => <option key={m}>{m}</option>)}</select></label>
          <label>Catatan<input value={pay.notes} onChange={e => setPay({ ...pay, notes: e.target.value })} /></label>
        </div>
        <div className="chip" style={{marginTop:16}}>Akan tercatat sebagai outflow kas kategori Supplier Payment dan mengurangi hutang PO.</div>
      </Modal>}
      {open && <Modal title={editing ? `Edit PO ${editing}` : "Buat Purchase Order"} subtitle={editing ? "Hanya PO yang belum disetujui dapat diubah" : "Otomatis masuk approval workflow"} onClose={() => { setOpen(false); setEditing(null); }} onSubmit={submit} testid="po-modal" wide>
        <div className="form-grid">
          <label className="form-full">Project<select required value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}>
            <option value="">Pilih project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></label>
          <label>Supplier<select required value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })}>
            <option value="">Pilih supplier</option>{suppliers.map(s => <option key={s.id} value={s.company}>{s.company}</option>)}
          </select></label>
          <label>Delivery Date<input type="date" required value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} /></label>
          <label className="form-full">Catatan<input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="opsional" /></label>
        </div>
        <small className="eyebrow" style={{display:"block",marginTop:16}}>MATERIAL YANG DIPESAN ({form.items.length})</small>
        <div className="po-items" data-testid="po-items">
          <div className="po-item head"><span>Material</span><span>Qty</span><span>Unit</span><span>Harga Satuan (Rp)</span><span>Subtotal</span><span /></div>
          {form.items.map((it, i) => <div className="po-item" key={i} data-testid={`po-item-${i}`}>
            <input required placeholder="Nama material" data-testid={`po-item-material-${i}`} value={it.material} onChange={e => setItem(i, { material: e.target.value })} />
            <input type="number" min="0.01" step="any" required data-testid={`po-item-qty-${i}`} value={it.quantity} onChange={e => setItem(i, { quantity: e.target.value })} />
            <input data-testid={`po-item-unit-${i}`} value={it.unit} onChange={e => setItem(i, { unit: e.target.value })} />
            <input type="number" min="0" required data-testid={`po-item-price-${i}`} value={it.unit_price} onChange={e => setItem(i, { unit_price: e.target.value })} />
            <b>{compact(Number(it.quantity || 0) * Number(it.unit_price || 0))}</b>
            <button type="button" className="danger-btn" data-testid={`po-item-remove-${i}`} disabled={form.items.length === 1} onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })}>×</button>
          </div>)}
        </div>
        <button type="button" className="ghost-btn" data-testid="po-add-item" style={{marginTop:8}} onClick={() => setForm({ ...form, items: [...form.items, { ...EMPTY_ITEM }] })}><Plus size={13} /> Tambah Material</button>
        <div className="chip" style={{marginTop:16,display:"flex"}} data-testid="po-total-preview">Total PO: {compact(poTotal)} · {form.items.length} material (tanpa PPN)</div>
      </Modal>}
    </div>
  );
}

// ============= Inventory =============
function InventoryPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { call.get("/inventory").then(setItems); }, []);
  return (
    <div>
      <PageHeader subtitle="Stok material lintas gudang dengan peringatan minimum stock." />
      <TableToolbar count={items.length} filters={["Semua","Aman","Stok Rendah"]} />
      <div className="panel data-panel">
        <div className="data-list">
          {items.map((m, i) => (
            <div className="data-item" data-testid={`inventory-item-${i}`} key={m.id}>
              <div className="data-main">
                <span className="row-icon"><Package size={16} /></span>
                <div><b>{m.name}</b><small>{m.sku} · {m.category} · {m.warehouse} · Min {m.minimum} {m.unit}</small></div>
              </div>
              <div className="data-value">{m.stock} {m.unit}</div>
              <span className={`status status-${slug(m.status)}`}>{m.status}</span>
              <ChevronRight size={16} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============= Suppliers =============
function SuppliersPage({ toast }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company:"", contact:"", phone:"", email:"", address:"", category:"Material", rating:4.0, payment_terms:"NET 30" });
  const load = () => call.get("/suppliers").then(setItems);
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await call.post("/suppliers", { ...form, rating: Number(form.rating) }); setOpen(false); load(); toast("Supplier ditambahkan"); }
    catch (e) { toast(e.response?.data?.detail || "Gagal", "error"); }
  };
  return (
    <div>
      <PageHeader subtitle="Database vendor dengan history performa & rating." />
      <TableToolbar count={items.length} onAdd={() => setOpen(true)} addLabel="Tambah Supplier" />
      <div className="panel data-panel">
        <div className="data-list">
          {items.map((s, i) => (
            <div className="data-item" data-testid={`suppliers-item-${i}`} key={s.id}>
              <div className="data-main">
                <span className="row-icon"><Truck size={16} /></span>
                <div><b>{s.company}</b><small>{s.contact} · {s.category} · {s.phone} · Term {s.payment_terms}</small></div>
              </div>
              <div className="data-value">⭐ {s.rating}</div>
              <span className="status status-active">Active</span>
              <ChevronRight size={16} />
            </div>
          ))}
        </div>
      </div>
      {open && <Modal title="Tambah Supplier" onClose={() => setOpen(false)} onSubmit={submit} testid="supplier-modal">
        <div className="form-grid">
          <label>Nama Perusahaan<input required value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></label>
          <label>Contact Person<input required value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} /></label>
          <label>Phone<input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></label>
          <label>Email<input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></label>
          <label>Kategori<select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
            {["Material Struktur","Ready Mix","MEP","Finishing","Equipment","Services"].map(s => <option key={s}>{s}</option>)}
          </select></label>
          <label>Payment Terms<input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} /></label>
          <label>Rating (1-5)<input type="number" step="0.1" min="1" max="5" value={form.rating} onChange={e => setForm({ ...form, rating: e.target.value })} /></label>
          <label className="form-full">Alamat<input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></label>
        </div>
      </Modal>}
    </div>
  );
}

// ============= Employees =============

// ============= Equipment =============
function EquipmentPage() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  useEffect(() => { call.get("/equipment").then(setItems); call.get("/projects").then(setProjects); }, []);
  return (
    <div>
      <PageHeader subtitle="Alat berat, operator, dan jam penggunaan per proyek." />
      <TableToolbar count={items.length} filters={["Semua","Rental","Own","Maintenance"]} />
      <div className="panel data-panel">
        <div className="data-list">
          {items.map((e, i) => {
            const project = projects.find(p => p.id === e.project_id);
            return (
              <div className="data-item" data-testid={`equipment-item-${i}`} key={e.id}>
                <div className="data-main">
                  <span className="row-icon"><Wrench size={16} /></span>
                  <div><b>{e.name}</b><small>{e.type} · {e.ownership} · Op {e.operator || "—"} · {e.hour_meter} jam · {project?.name || "Pool"}</small></div>
                </div>
                <div className="data-value">{e.rental_price ? `${compact(e.rental_price)}/bln` : "Owned"}</div>
                <span className={`status status-${slug(e.status)}`}>{e.status}</span>
                <ChevronRight size={16} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============= Invoices =============
function InvoicesPage({ toast }) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [company, setCompany] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY_INV = { project_id:"", client:"", amount:0, due_date:"", tax_pct:11, retention_pct:5, description:"", billing_mode:"manual", until_date:"", from_date:"", period_start:"", period_end:"" };
  const [form, setForm] = useState(EMPTY_INV);
  const [ack, setAck] = useState(false);
  const selProject = projects.find(x => x.id === form.project_id);
  const isCostFee = !editing && selProject?.contract_type === "Cost and Fee" && form.billing_mode === "cost_fee";
  const load = () => call.get("/invoices").then(setItems);
  useEffect(() => { load(); call.get("/projects").then(setProjects); call.get("/settings").then(setCompany).catch(() => {}); }, []);
  const submit = async () => {
    const body = { ...form, amount: Number(form.amount), tax_pct: Number(form.tax_pct), retention_pct: Number(form.retention_pct), acknowledge_cap: ack };
    if (editing) { delete body.billing_mode; delete body.until_date; delete body.from_date; }
    try { if (editing) await call.put(`/invoices/${editing}`, body); else await call.post("/invoices", body); setOpen(false); setEditing(null); setAck(false); load(); toast(editing ? "Invoice diperbarui" : "Invoice dibuat"); }
    catch (e) { toast(formatError(e), "error"); }
  };
  const startEdit = (inv) => { setEditing(inv.id); setForm(Object.fromEntries(Object.keys(EMPTY_INV).map(k => [k, inv[k] ?? EMPTY_INV[k]]))); setOpen(true); };
  const remove = async (inv) => { try { await call.delete(`/invoices/${inv.id}`); load(); toast("Invoice dihapus (diarsipkan)"); } catch (e) { toast(formatError(e), "error"); } };
  const payInvoice = async (id, amount) => {
    try { await call.post("/payments", { reference_type:"invoice", reference_id:id, amount, date:new Date().toISOString().slice(0,10), method:"Transfer" }); load(); toast("Pembayaran dicatat"); }
    catch (e) { toast("Gagal", "error"); }
  };
  const [filter, setFilter] = useState("Semua");
  const INV_FILTERS = ["Semua","Draft","Sent","Paid","Overdue"];
  const matchFilter = (inv, f) => f === "Semua" || (f === "Paid" ? ["Paid", "Partially Paid"].includes(inv.status) : inv.status === f);
  const counts = Object.fromEntries(INV_FILTERS.slice(1).map(f => [f, items.filter(inv => matchFilter(inv, f)).length]));
  const visible = items.filter(inv => matchFilter(inv, filter));
  const sendInvoice = async (inv) => { try { await call.post(`/invoices/${inv.id}/send`, {}); load(); toast(`Invoice ${inv.code} ditandai terkirim`); } catch (e) { toast(formatError(e), "error"); } };
  const exportPdf = async (inv) => { try { await exportInvoicePDF(inv, company, projects.find(p => p.id === inv.project_id)); toast(`PDF ${inv.code} diunduh`); } catch (e) { toast("Gagal membuat PDF", "error"); } };
  return (
    <div>
      <PageHeader subtitle="Tagihan progress, retention, dan status pembayaran." />
      <TableToolbar count={items.length} filters={INV_FILTERS} counts={counts} onFilter={setFilter} onAdd={() => { setEditing(null); setForm(EMPTY_INV); setOpen(true); }} addLabel="Buat Invoice" />
      <div className="panel data-panel">
        <div className="data-list">
          {visible.length === 0 && <div className="empty-state" data-testid="invoices-empty">Tidak ada invoice dengan status {filter}.</div>}
          {visible.map((inv, i) => (
            <div className="data-item" data-testid={`invoices-item-${i}`} key={inv.id}>
              <div className="data-main">
                <span className="row-icon"><Receipt size={16} /></span>
                <div><b>{inv.code}</b><small>{inv.client} · {inv.description} · {inv.period_start || inv.period_end ? `periode ${date(inv.period_start)} – ${date(inv.period_end)} · ` : ""}due {date(inv.due_date)}{inv.paid > 0 && inv.status !== "Paid" ? ` · terbayar ${compact(inv.paid)}` : ""}</small></div>
              </div>
              <div className="data-value">{compact(inv.total)}</div>
              {inv.cost_fee_breakdown && <span className={`status ${inv.cost_fee_breakdown.cap_level === "exceeded" ? "status-rejected" : inv.cost_fee_breakdown.cap_level === "warning" ? "status-pending" : "status-quotation"}`} title={`Cap terpakai ${inv.cost_fee_breakdown.cap_used_pct}%`} data-testid={`invoice-costfee-${i}`}>Cost+Fee{inv.cost_fee_breakdown.cap_used_pct != null ? ` ${inv.cost_fee_breakdown.cap_used_pct}%` : ""}</span>}
              <span className={`status status-${slug(inv.status)}`} data-testid={`invoice-status-${i}`}>{inv.status}</span>
              <div className="row-actions">
                <button className="ghost-btn pdf-btn" data-testid={`invoice-pdf-${i}`} onClick={() => exportPdf(inv)} title="Export PDF"><Download size={14} /> PDF</button>
                {inv.status === "Draft" && <button className="ghost-btn" data-testid={`send-invoice-${i}`} onClick={() => sendInvoice(inv)} title="Tandai sudah dikirim ke klien">Kirim</button>}
                {inv.status !== "Paid" && <button className="ghost-btn" data-testid={`pay-invoice-${i}`} onClick={() => payInvoice(inv.id, inv.total - (inv.paid || 0))}>Bayar</button>}
                <RowEditActions testid={`invoice-${i}`} locked={inv.status === "Paid" || inv.paid > 0} onEdit={() => startEdit(inv)} onDelete={() => remove(inv)} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {open && <Modal title={editing ? `Edit ${editing}` : "Buat Invoice"} subtitle={editing ? "Invoice yang sudah dibayar tidak dapat diubah" : undefined} onClose={() => { setOpen(false); setEditing(null); }} onSubmit={submit} wide={isCostFee} testid="invoice-modal">
        <div className="form-grid">
          <label>Project<select required data-testid="invoice-project-select" value={form.project_id} onChange={e => { const p = projects.find(x => x.id === e.target.value); setForm({ ...form, project_id: e.target.value, client: p?.client || "", billing_mode: p?.contract_type === "Cost and Fee" ? "cost_fee" : "manual" }); setAck(false); }}>
            <option value="">Pilih project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.contract_type === "Cost and Fee" ? " · Cost and Fee" : ""}</option>)}
          </select></label>
          <label>Client<input value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} /></label>
          {!editing && selProject?.contract_type === "Cost and Fee" && <label>Mode Tagihan<select data-testid="invoice-billing-mode" value={form.billing_mode} onChange={e => setForm({ ...form, billing_mode: e.target.value })}><option value="cost_fee">Cost and Fee (otomatis)</option><option value="manual">Manual</option></select></label>}
          {isCostFee ? <>
            <label>Biaya Dari Tanggal <small style={{textTransform:"none",fontWeight:500,color:"#9aa5b4"}}>(kosong = awal)</small><input type="date" data-testid="invoice-from-date" value={form.from_date} max={form.until_date || undefined} onChange={e => setForm({ ...form, from_date: e.target.value })} /></label>
            <label>Biaya s/d Tanggal <small style={{textTransform:"none",fontWeight:500,color:"#9aa5b4"}}>(kosong = semua)</small><input type="date" data-testid="invoice-until-date" value={form.until_date} min={form.from_date || undefined} onChange={e => setForm({ ...form, until_date: e.target.value })} /></label>
          </> : <>
            <label>Amount (Rp)<input type="number" required data-testid="invoice-amount-input" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></label>
            <label>Periode Dari <small style={{textTransform:"none",fontWeight:500,color:"#9aa5b4"}}>(opsional)</small><input type="date" data-testid="invoice-period-start" value={form.period_start} max={form.period_end || undefined} onChange={e => setForm({ ...form, period_start: e.target.value })} /></label>
            <label>Periode Sampai<input type="date" data-testid="invoice-period-end" value={form.period_end} min={form.period_start || undefined} onChange={e => setForm({ ...form, period_end: e.target.value })} /></label>
          </>}
          <label>Due Date (Tenggat Bayar)<input type="date" required data-testid="invoice-due-date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
            <span className="quick-days" data-testid="invoice-due-quick">{[7, 14, 30].map(d => <button type="button" key={d} className="chip-btn" data-testid={`invoice-due-plus-${d}`} onClick={() => { const b = new Date((isCostFee ? form.until_date : form.period_end) || Date.now()); b.setDate(b.getDate() + d); setForm({ ...form, due_date: b.toISOString().slice(0, 10) }); }}>+{d} hari</button>)}</span>
          </label>
          <label>PPN (%)<input type="number" value={form.tax_pct} onChange={e => setForm({ ...form, tax_pct: e.target.value })} /></label>
          <label>Retention (%)<input type="number" value={form.retention_pct} onChange={e => setForm({ ...form, retention_pct: e.target.value })} /></label>
          <label className="form-full">Deskripsi<input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={isCostFee ? "Opsional — otomatis: Tagihan Cost and Fee periode …" : "Progress payment 15%"} /></label>
        </div>
        {isCostFee && <CostFeePanel projectId={form.project_id} untilDate={form.until_date} fromDate={form.from_date} taxPct={form.tax_pct} acknowledge={ack} setAcknowledge={setAck} />}
      </Modal>}
    </div>
  );
}

// ============= Expenses =============
function ExpensesPage({ toast }) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id:"", category:"Transport", description:"", amount:0, date:new Date().toISOString().slice(0,10) });
  const [files, setFiles] = useState([]);
  const [photosByExpense, setPhotosByExpense] = useState({});
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  const loadPhotos = () => call.get("/expenses/photos/all").then(list => { const by = {}; list.forEach(p => (by[p.expense_id] = by[p.expense_id] || []).push(p)); setPhotosByExpense(by); }).catch(() => {});
  const load = () => { call.get("/expenses").then(setItems); loadPhotos(); };
  const [company, setCompany] = useState(null);
  const [recap, setRecap] = useState({ open: true, start: new Date().toISOString().slice(0, 8) + "01", end: new Date().toISOString().slice(0, 10), detail: true, project_id: "all", photos: true });
  const [exporting, setExporting] = useState(false);
  useEffect(() => { load(); call.get("/projects").then(setProjects); call.get("/settings").then(setCompany).catch(() => {}); }, []);
  const recapGroups = (() => {
    const inRange = items.filter(e => (!recap.start || e.date >= recap.start) && (!recap.end || e.date <= recap.end) && (recap.project_id === "all" || (e.project_id || "") === (recap.project_id === "none" ? "" : recap.project_id)));
    const by = {};
    inRange.forEach(e => { const k = e.project_id || ""; const g = by[k] || (by[k] = { project_id: k, name: projects.find(p => p.id === k)?.name || (k ? k : "Tanpa project (kantor / payroll)"), items: [], total: 0, cats: {} }); g.items.push(e); g.total += e.amount; g.cats[e.category] = g.cats[e.category] || { category: e.category, amount: 0, count: 0 }; g.cats[e.category].amount += e.amount; g.cats[e.category].count++; });
    return Object.values(by).map(g => ({ ...g, items: [...g.items].sort((a, b) => a.date.localeCompare(b.date)), categories: Object.values(g.cats).sort((a, b) => b.amount - a.amount) })).sort((a, b) => b.total - a.total);
  })();
  const recapTotal = recapGroups.reduce((s, g) => s + g.total, 0);
  const recapProjectName = recap.project_id === "all" ? "" : recap.project_id === "none" ? "Tanpa project" : (projects.find(p => p.id === recap.project_id)?.name || recap.project_id);
  const recapProjectOptions = [...new Set(items.map(e => e.project_id || ""))].map(id => ({ id: id || "none", name: id ? (projects.find(p => p.id === id)?.name || id) : "Tanpa project (kantor / payroll)" })).sort((a, b) => a.name.localeCompare(b.name));
  const exportRecap = async () => { setExporting(true); try { await exportExpenseRecapPDF(recapGroups, { start: recap.start, end: recap.end, company, withDetail: recap.detail, projectName: recapProjectName, photosByExpense: recap.photos ? photosByExpense : null }); toast("PDF rekap expense diunduh"); } catch (e) { console.error(e); toast("Gagal membuat PDF", "error"); } finally { setExporting(false); } };
  const submit = async () => {
    setSaving(true);
    try {
      const created = await call.post("/expenses", { ...form, amount: Number(form.amount) });
      let failed = 0;
      const photos = files.filter(Boolean);
      for (const f of photos) { const fd = new FormData(); fd.append("file", f.file); fd.append("taken_at", f.taken_at); try { await call.upload(`/expenses/${created.id}/photos`, fd); } catch { failed++; } }
      setOpen(false); setFiles([]); load();
      toast(failed ? `Expense dicatat, ${failed} foto gagal diunggah` : photos.length ? `Expense & ${photos.length} foto bukti tersimpan` : "Expense dicatat", failed ? "error" : undefined);
    }
    catch (e) { toast(e.response?.data?.detail || "Gagal", "error"); }
    finally { setSaving(false); }
  };
  const addReceipt = async (expenseId, e) => {
    const list = Array.from(e.target.files || []); e.target.value = "";
    try { for (const f of list) { const fd = new FormData(); fd.append("file", f); fd.append("taken_at", new Date(f.lastModified).toISOString()); await call.upload(`/expenses/${expenseId}/photos`, fd); } await loadPhotos(); toast(`${list.length} foto bukti ditambahkan`); }
    catch (err) { toast(err.response?.data?.detail || "Upload foto gagal", "error"); }
  };
  const deleteReceipt = async (photo) => {
    try { await call.delete(`/expenses/${photo.expense_id}/photos/${photo.id}`); await loadPhotos(); toast("Foto bukti dihapus"); }
    catch (err) { toast(err.response?.data?.detail || "Gagal menghapus foto", "error"); }
  };
  const [scanning, setScanning] = useState(false);
  const [scanInfo, setScanInfo] = useState(null);
  const scanReceipt = async () => {
    const first = files.find(Boolean);
    if (!first) return;
    setScanning(true); setScanInfo(null);
    try {
      const fd = new FormData(); fd.append("file", first.file);
      const r = await call.upload("/expenses/scan-receipt", fd);
      setForm(f => ({ ...f, amount: r.amount || f.amount, description: r.description || f.description, date: r.date || f.date, category: r.category || f.category }));
      setScanInfo(r); toast(r.amount ? `Nota terbaca: ${money(r.amount)}${r.vendor ? ` · ${r.vendor}` : ""}` : "Nota terbaca, periksa kembali nominalnya", r.amount ? undefined : "error");
    } catch (err) { toast(err.response?.data?.detail || "AI gagal membaca nota", "error"); }
    finally { setScanning(false); }
  };
  return (
    <div>
      <PageHeader subtitle="Biaya operasional tambahan — langsung terkait project cost." />
      <div className="panel recap-panel" data-testid="expense-recap">
        <div className="recap-head">
          <div><span className="eyebrow">REKAP EXPENSE PER PROJECT</span><h3>Total tercatat <b data-testid="expense-recap-total">{money(recapTotal)}</b> <small className="muted">· {recapGroups.length} project · {recapGroups.reduce((s, g) => s + g.items.length, 0)} transaksi</small></h3></div>
          <div className="recap-controls">
            <label>Project<select data-testid="expense-recap-project" value={recap.project_id} onChange={e => setRecap({ ...recap, project_id: e.target.value })}>
              <option value="all">Semua project</option>{recapProjectOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select></label>
            <label>Dari<input type="date" data-testid="expense-recap-start" value={recap.start} onChange={e => setRecap({ ...recap, start: e.target.value })} /></label>
            <label>Sampai<input type="date" data-testid="expense-recap-end" value={recap.end} onChange={e => setRecap({ ...recap, end: e.target.value })} /></label>
            <button className="ghost-btn pdf-btn" data-testid="expense-recap-all" onClick={() => setRecap({ ...recap, start: "", end: "" })}>Semua periode</button>
            <label className="recap-check"><input type="checkbox" checked={recap.detail} onChange={e => setRecap({ ...recap, detail: e.target.checked })} /> Sertakan rincian transaksi</label>
            <label className="recap-check"><input type="checkbox" data-testid="expense-recap-photos" checked={recap.photos} onChange={e => setRecap({ ...recap, photos: e.target.checked })} /> Sertakan foto bukti</label>
            <button className="primary-btn small" disabled={exporting || recapGroups.length === 0} data-testid="expense-recap-pdf" onClick={exportRecap}><Download size={14} /> {exporting ? "Menyusun…" : "Export PDF"}</button>
          </div>
        </div>
        {recapGroups.length === 0 ? <small className="muted">Tidak ada expense pada rentang tanggal ini.</small> : <div className="boq-wrap" style={{ marginTop: 10 }}><table className="boq-table" data-testid="expense-recap-table">
          <thead><tr><th>Project</th><th className="num">Transaksi</th><th>Kategori terbesar</th><th className="num">Total</th><th className="num">%</th></tr></thead>
          <tbody>{recapGroups.map((g, i) => <tr key={g.project_id || "none"} data-testid={`expense-recap-row-${i}`}><td><b>{g.name}</b></td><td className="num">{g.items.length}</td><td>{g.categories[0] ? `${g.categories[0].category} · ${compact(g.categories[0].amount)}` : "-"}</td><td className="num"><b>{money(g.total)}</b></td><td className="num">{recapTotal ? Math.round(g.total / recapTotal * 100) : 0}%</td></tr>)}</tbody>
          <tfoot><tr className="boq-grand"><td colSpan={3}>TOTAL SELURUH PROJECT</td><td className="num">{money(recapTotal)}</td><td className="num">100%</td></tr></tfoot>
        </table></div>}
      </div>
      <TableToolbar count={items.length} filters={["Semua","Transport","Fuel","Accommodation"]} onAdd={() => setOpen(true)} addLabel="Catat Expense" />
      <div className="panel data-panel">
        <div className="data-list">
          {items.map((e, i) => {
            const project = projects.find(p => p.id === e.project_id);
            const ph = photosByExpense[e.id] || [];
            return (
              <div className="data-item" data-testid={`expenses-item-${i}`} key={e.id}>
                <div className="data-main">
                  <span className="row-icon"><CircleDollarSign size={16} /></span>
                  <div><b>{e.description}</b><small>{project?.name || e.project_id} · {e.category} · {date(e.date)}</small></div>
                </div>
                <div className="data-value">{compact(e.amount)}</div>
                <div style={{display:"flex", gap:6, alignItems:"center"}}>
                  <button type="button" className="ghost-btn small" data-testid={`expense-photos-${i}`} disabled={!ph.length} onClick={() => setViewing(e)} title="Lihat foto bukti"><ImageIcon size={13} /> {ph.length}</button>
                  <label className={`ghost-btn small ${ph.length >= 2 ? "disabled" : ""}`} style={{cursor: ph.length >= 2 ? "not-allowed" : "pointer", opacity: ph.length >= 2 ? .5 : 1}} data-testid={`expense-add-photo-${i}`} title={ph.length >= 2 ? "Maksimal 2 foto" : "Tambah foto bukti"}><Camera size={13} /><input type="file" accept="image/*" hidden disabled={ph.length >= 2} onChange={ev => addReceipt(e.id, ev)} /></label>
                </div>
                <span className="status status-active">Posted</span>
              </div>
            );
          })}
        </div>
      </div>
      {viewing && <Modal title={`Foto Bukti — ${viewing.description}`} onClose={() => setViewing(null)} testid="expense-photos-modal">
        <small className="muted">{money(viewing.amount)} · {viewing.category} · {date(viewing.date)}</small>
        <div style={{marginTop:10}}><PhotoGallery photos={photosByExpense[viewing.id] || []} title="Bukti expense" onDelete={deleteReceipt} /></div>
      </Modal>}
      {open && <Modal title="Catat Expense" onClose={() => { setOpen(false); setFiles([]); }} onSubmit={submit} busy={saving} submitLabel={saving ? "Menyimpan…" : "Simpan"} testid="expense-modal">
        <div className="form-grid">
          <label>Project<select required value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}>
            <option value="">Pilih project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></label>
          <label>Kategori<select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
            {["Material","Labor","Equipment","Transport","Accommodation","Office","Fuel","Subcontractor","Miscellaneous"].map(s => <option key={s}>{s}</option>)}
          </select></label>
          <label>Amount (Rp)<input type="number" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></label>
          <label>Date<input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></label>
          <label className="form-full">Deskripsi<input required value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></label>
          <div className="form-full"><small style={{display:"block", marginBottom:6, color:"#5b6878", fontWeight:600}}>Foto bukti (nota / kwitansi) — maks. 2 foto per catatan</small>
            <TwoSlotPicker files={files} setFiles={setFiles} labels={["Foto bukti 1", "Foto bukti 2"]} />
          </div>
          {files.some(Boolean) && <div className="form-full ai-scan-row" data-testid="receipt-scan-row">
            <button type="button" className="ghost-btn ai-scan-btn" data-testid="receipt-scan-button" disabled={scanning} onClick={scanReceipt}><Sparkles size={14} /> {scanning ? "Membaca nota…" : "Isi otomatis dari foto nota (AI)"}</button>
            <small className="muted">{scanInfo ? `Terbaca: ${scanInfo.vendor || "-"} · ${money(scanInfo.amount || 0)}${scanInfo.date ? ` · ${date(scanInfo.date)}` : ""} · ${scanInfo.category}. Periksa kembali sebelum menyimpan.` : "AI membaca foto pertama dan mengisi nominal, deskripsi, tanggal & kategori."}</small>
          </div>}
        </div>
      </Modal>}
    </div>
  );
}

// ============= Reports =============
function ReportsPage({ toast }) {
  const [pnl, setPnl] = useState([]);
  const [tab, setTab] = useState("profit");
  useEffect(() => { call.get("/reports/project-pnl").then(setPnl); }, []);
  const totalCV = pnl.reduce((s, p) => s + p.contract_value, 0);
  const totalCost = pnl.reduce((s, p) => s + p.actual_cost, 0);
  const totalProfit = pnl.reduce((s, p) => s + p.gross_profit, 0);
  return (
    <div>
      <PageHeader subtitle="Project profit accounting, P&L, cost report, dan ringkasan portofolio." />
      <div className="module-toolbar"><div className="filter-tabs">
        <button data-testid="reports-tab-profit" className={tab === "profit" ? "selected" : ""} onClick={() => setTab("profit")}>Profit Accounting</button>
        <button data-testid="reports-tab-pnl" className={tab === "pnl" ? "selected" : ""} onClick={() => setTab("pnl")}>P&L Ringkas</button>
      </div></div>
      {tab === "profit" ? <ProjectProfitReport toast={toast} /> : <div className="panel">
        <div className="panel-head"><div><span className="eyebrow">PROJECT PROFIT & LOSS</span><h3>Ringkasan P&L per proyek</h3></div></div>
        <div style={{overflowX:"auto",marginTop:16}}>
          <table className="report-table" data-testid="pnl-table">
            <thead><tr><th>Project</th><th>Code</th><th className="num">Contract Value</th><th className="num">Budget</th><th className="num">Actual Cost</th><th className="num">Gross Profit</th><th className="num">Margin</th><th className="num">Progress</th></tr></thead>
            <tbody>
              {pnl.map(p => (
                <tr key={p.code}>
                  <td><b>{p.project}</b></td>
                  <td>{p.code}</td>
                  <td className="num">{compact(p.contract_value)}</td>
                  <td className="num">{compact(p.budget)}</td>
                  <td className="num">{compact(p.actual_cost)}</td>
                  <td className="num" style={{color:"#1f7b3f",fontWeight:600}}>{compact(p.gross_profit)}</td>
                  <td className="num">{p.margin}%</td>
                  <td className="num">{p.progress}%</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan="2">TOTAL PORTFOLIO</td><td className="num">{compact(totalCV)}</td><td className="num">—</td><td className="num">{compact(totalCost)}</td><td className="num">{compact(totalProfit)}</td><td className="num">{totalCV ? Math.round(totalProfit/totalCV*100) : 0}%</td><td className="num">—</td></tr></tfoot>
          </table>
        </div>
      </div>}
    </div>
  );
}

export default App;
